import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import DashboardLayout from '../layouts/DashboardLayout';
import AdCarousel from '../components/AdCarousel';
import axios from 'axios';
import Swal from 'sweetalert2';
import HizliAra from '../assets/images/hizli-ara.png';
import GununMenusu from '../assets/images/gunun-menusu.png';
import DolapSef from '../assets/images/dolap-sef.png';
import BuzdolabimdaAra from '../assets/images/buzdolabimda-ara.png';
import YaptigimTarifler from '../assets/images/begeni.png';
import PlanlaImg from '../assets/images/planla.png';
import OzelGunlerImg from '../assets/images/ozel-gunler.png';
import AthleteImg from '../assets/images/athlete-main.png';
import DietImg from '../assets/images/diet-main.png';
import AllergenImg from '../assets/images/allergen-main.png';
import AlakartImg from '../assets/images/avangart-main.png';
import HealthPlusImg from '../assets/health-plus-card.png';
import IntelligenceBanner from '../components/IntelligenceBanner';
import RecipeMenuGroup from '../components/RecipeMenuGroup';
import MenuCompletionBanner from '../components/MenuCompletionBanner';
import AlakartCountdownCard from '../components/AlakartCountdownCard';
import SystemNudgeCarousel from '../components/SystemNudgeCarousel';
import { promptKitchenReset } from '../utils/kitchenReset';
import BreakfastRecommendationModal from '../components/BreakfastRecommendationModal';
import RecipeModal from '../components/RecipeModal';
import useMenuCompletionState from '../hooks/useMenuCompletionState';
import { resolveClientMealSlot, mealSlotLabel } from '../utils/mealSlotUi';
import { calculateRecipeCostDisplay } from '../utils/recipePricing';
import { pickMessageByMode, resolveOnboardingMessageMode } from '../lib/onboardingMessageMode';
import { hydrateRecipeForModal } from '../utils/recipeModalData';
import { detectCurrentPushDeviceKind, getServerPushStatus } from '../lib/push/webPushClient';

const onboardingCostHydrationCache = new Map();

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TODAY_VIEW_PRIORITY = ['allergen', 'diet', 'athlete', 'alakart', 'smart_search'];
const RESCUE_BANNER_PROMINENT_MS = 5 * 60 * 1000;
const RESCUE_SCROLL_COLLAPSE_PX = 120;
const BABY_DETAIL_DEFER_PREFIX = 'bbtv_baby_detail_defer';
const BABY_SIGNAL_LOGIN_GATE_PREFIX = 'bbtv_baby_signal_login_gate';
const BABY_SIGNAL_SESSION_MARK_PREFIX = 'bbtv_baby_signal_session_mark';
const LUNCH_SERVING_OPTIONS = [
  { value: 1, label: '1 kişi' },
  { value: 2, label: '2 kişi' },
  { value: 3, label: '3 kişi' },
  { value: 4, label: '4 kişi' },
  { value: 'family_size', label: 'Aile kişi sayısı kadar' }
];
const LUNCH_MODE_OPTIONS = [
  {
    value: 'light_practical_lunch',
    label: 'Hafif / pratik öğlen öner',
    description: 'Yan yemek havuzundan hafif ve hızlı öğlen önerileri üretirim.'
  },
  {
    value: 'extra_dinner_portion',
    label: 'Akşam yemeğini fazla porsiyon planla',
    description: 'Akşamı öğlen için seçtiğin kişi sayısı kadar fazla hesaplarım.'
  },
  {
    value: 'separate_lunch_plan',
    label: 'Öğlen için ayrı yemek öner',
    description: 'Öğlen için akşamdan bağımsız ayrı öneri üretirim.',
    badge: 'Yüksek Maliyet'
  },
  {
    value: 'leftover_only',
    label: 'Öğlen önerme, kalanlardan yerim',
    description: 'Öğlen için menü açmam, sadece yumuşak bir hatırlatma bırakırım.'
  }
];

const VIEW_META = {
  smart_search: {
    label: 'Akıllı Mutfak',
    description: 'Bugün için tam eşleşmeli günlük menü.',
    href: '/quick-search'
  },
  allergen: {
    label: 'Alerjen Mutfağı',
    description: 'Bugün için güvenli içerikli günlük menü.',
    href: '/allergen-kitchen'
  },
  diet: {
    label: 'Diyet Mutfağı',
    description: 'Bugünkü hedefinize uygun öğünler.',
    href: '/diet-kitchen'
  },
  athlete: {
    label: 'Sporcu Mutfağı',
    description: 'Bugün için performans odaklı öğünler.',
    href: '/athlete-kitchen'
  },
  alakart: {
    label: 'Alakart Menü',
    description: 'Planınızdan bugüne düşen öneriler.',
    href: '/alakart-menu'
  }
};

const todayKey = () => new Date().toISOString().split('T')[0];
const DASHBOARD_TODAY_CACHE_PREFIX = 'dashboard_today_views_v4';

function getCurrentPlanningPeriod() {
  const hour = new Date().getHours();
  return hour < 15 ? 'lunch' : 'dinner';
}

function getRecipeMissingCount(recipe) {
  return recipe?.match?.missingCount ?? recipe?.missingCount ?? (recipe?.missingIngredients?.length || 0);
}

function isFullyMatchedRecipe(recipe) {
  return !!recipe && getRecipeMissingCount(recipe) === 0;
}

function lower(value) {
  return String(value || '').toLocaleLowerCase('tr-TR');
}

function inferDashboardSlot(recipe) {
  const serveStyle = lower(recipe?.serveStyle);
  const title = lower(recipe?.title || recipe?.name);
  const categoryIndex = Number(recipe?.categoryIndex);

  if (serveStyle.includes('çorba') || serveStyle.includes('corba') || title.includes('çorba') || title.includes('corba') || categoryIndex === 0) {
    return 'soup';
  }
  if (serveStyle.includes('salata') || serveStyle.includes('meze') || title.includes('salata') || title.includes('meze') || categoryIndex === 2) {
    return 'salad';
  }
  if (serveStyle.includes('tatlı') || serveStyle.includes('tatli') || serveStyle.includes('dessert') || categoryIndex === 3) {
    return 'dessert';
  }
  return 'main';
}

function buildStructuredFallbackFromRecipes(recipes = []) {
  const fullyMatched = recipes.filter(isFullyMatchedRecipe);
  if (!fullyMatched.length) return null;

  const buckets = {
    soup: null,
    main: null,
    salad: null
  };

  fullyMatched.forEach((recipe) => {
    const slot = inferDashboardSlot(recipe);
    if (slot in buckets && !buckets[slot]) {
      buckets[slot] = recipe;
    }
  });

  if (!buckets.main && !buckets.soup && !buckets.salad) return null;

  return {
    soup: buckets.soup,
    main: buckets.main,
    salad: buckets.salad,
    pair: Array.isArray(buckets.main?.suggestedPairings) ? buckets.main.suggestedPairings.filter(Boolean) : []
  };
}

function normalizeStructuredView(sourceKey, payload) {
  const structured = payload?.structuredMenu
    ? payload.structuredMenu
    : buildStructuredFallbackFromRecipes(payload?.recipes || []);
  if (!structured) return null;

  const coreMenu = {
    soup: isFullyMatchedRecipe(structured.soup) ? structured.soup : null,
    main: isFullyMatchedRecipe(structured.main) ? structured.main : null,
    salad: isFullyMatchedRecipe(structured.salad) ? structured.salad : null,
    pair: Array.isArray(structured.pair) ? structured.pair.filter(Boolean) : []
  };

  if (!coreMenu.pair.length && Array.isArray(coreMenu.main?.suggestedPairings)) {
    coreMenu.pair = coreMenu.main.suggestedPairings.filter(Boolean);
  }

  if (!payload?.structuredMenu) {
    const fallback = buildStructuredFallbackFromRecipes(payload?.recipes || []);
    if (fallback) {
      coreMenu.soup = coreMenu.soup || fallback.soup || null;
      coreMenu.main = coreMenu.main || fallback.main || null;
      coreMenu.salad = coreMenu.salad || fallback.salad || null;
      if (!coreMenu.pair.length && Array.isArray(fallback.pair)) {
        coreMenu.pair = fallback.pair;
      }
    }
  }

  const hasReliableCore = coreMenu.main || coreMenu.soup || coreMenu.salad;
  if (!hasReliableCore) return null;

  return {
    key: sourceKey,
    type: 'structured',
    meta: VIEW_META[sourceKey],
    payloadMeta: payload?.meta || null,
    structuredMenu: coreMenu,
    recipes: [coreMenu.soup, coreMenu.main, coreMenu.salad, ...coreMenu.pair].filter(Boolean)
  };
}

function pickTodayPlanDay(weekPlan = []) {
  if (!Array.isArray(weekPlan) || !weekPlan.length) return null;
  return weekPlan.find((day) => day.date === todayKey()) || weekPlan[0] || null;
}

function normalizeMealDayView(sourceKey, day) {
  if (!day?.meals) return null;

  const meals = ['breakfast', 'lunch', 'dinner']
    .map((mealType) => {
      const recipes = Array.isArray(day.meals?.[mealType]) ? day.meals[mealType].filter(isFullyMatchedRecipe) : [];
      return recipes.length ? { mealType, recipes } : null;
    })
    .filter(Boolean);

  if (!meals.length) return null;

  return {
    key: sourceKey,
    type: 'meals',
    meta: VIEW_META[sourceKey],
    meals,
    date: day.date
  };
}

function normalizeAlakartView(plan) {
  const generatedDays = plan?.generatedDays;
  if (!generatedDays || typeof generatedDays !== 'object') return null;

  const firstDay = generatedDays[todayKey()]
    ? { date: todayKey(), slots: generatedDays[todayKey()] }
    : Object.entries(generatedDays)
      .filter(([key, value]) => key !== '_meta' && value && typeof value === 'object')
      .map(([date, slots]) => ({ date, slots }))
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))[0];

  if (!firstDay?.slots) return null;

  const structuredMenu = {
    soup: firstDay.slots.SOUP || firstDay.slots.soup || null,
    main: firstDay.slots.MAIN || firstDay.slots.main || null,
    salad: firstDay.slots.SALAD || firstDay.slots.salad || null,
    pair: [firstDay.slots.SIDE || firstDay.slots.PAIR || firstDay.slots.pair].filter(Boolean)
  };

  return normalizeStructuredView('alakart', { structuredMenu });
}

function getTodayViewHeadline(view) {
  if (!view?.meta?.label) return 'Günün Menüsü';
  return `Günün Mutfağı - (${view.meta.label} Planlı)`;
}

function computeBabyAgeGroup(birthDate) {
  if (!birthDate) return null;
  const start = new Date(birthDate);
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (months < 0) return null;
  if (months < 8) return '6-8';
  if (months < 12) return '8-12';
  if (months < 24) return '12-24';
  return '2+';
}

function collectViewRecipes(view) {
  if (!view) return [];
  if (view.type === 'structured') {
    const menu = view.structuredMenu || {};
    return [menu.soup, menu.main, menu.salad, ...(Array.isArray(menu.pair) ? menu.pair : [])]
      .filter(Boolean);
  }
  if (view.type === 'meals') {
    return (view.meals || []).flatMap((meal) => meal?.recipes || []).filter(Boolean);
  }
  return Array.isArray(view.recipes) ? view.recipes.filter(Boolean) : [];
}

function patchViewWithRecipes(view, recipeById) {
  if (!view) return view;
  const resolve = (recipe) => {
    const key = String(recipe?.id || '').trim();
    return key && recipeById.has(key) ? recipeById.get(key) : recipe;
  };

  if (view.type === 'structured') {
    const menu = view.structuredMenu || {};
    const nextMenu = {
      soup: resolve(menu.soup),
      main: resolve(menu.main),
      salad: resolve(menu.salad),
      pair: Array.isArray(menu.pair) ? menu.pair.map(resolve).filter(Boolean) : []
    };

    return {
      ...view,
      structuredMenu: nextMenu,
      recipes: [nextMenu.soup, nextMenu.main, nextMenu.salad, ...nextMenu.pair].filter(Boolean)
    };
  }

  if (view.type === 'meals') {
    const nextMeals = (view.meals || []).map((meal) => ({
      ...meal,
      recipes: (meal.recipes || []).map(resolve).filter(Boolean)
    })).filter((meal) => meal.recipes.length > 0);

    return {
      ...view,
      meals: nextMeals,
      recipes: nextMeals.flatMap((meal) => meal.recipes || [])
    };
  }

  return {
    ...view,
    recipes: (view.recipes || []).map(resolve).filter(Boolean)
  };
}

function enforceStructuredStockGuard(view) {
  if (!view || view.type !== 'structured') return view;
  const menu = view.structuredMenu || {};
  const nextMenu = {
    soup: isFullyMatchedRecipe(menu.soup) ? menu.soup : null,
    main: isFullyMatchedRecipe(menu.main) ? menu.main : null,
    salad: isFullyMatchedRecipe(menu.salad) ? menu.salad : null,
    pair: Array.isArray(menu.pair) ? menu.pair.filter(Boolean) : []
  };

  if (!nextMenu.pair.length && Array.isArray(nextMenu.main?.suggestedPairings)) {
    nextMenu.pair = nextMenu.main.suggestedPairings.filter(Boolean);
  }

  const hasAnyRecipe = !!(nextMenu.soup || nextMenu.main || nextMenu.salad || nextMenu.pair.length);
  if (!hasAnyRecipe) return null;

  return {
    ...view,
    structuredMenu: nextMenu,
    recipes: [nextMenu.soup, nextMenu.main, nextMenu.salad, ...nextMenu.pair].filter(Boolean)
  };
}

function parseTimeToMinutes(timeStr) {
  if (!timeStr || typeof timeStr !== 'string' || !timeStr.includes(':')) return null;
  const [hour, minute] = timeStr.split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return hour * 60 + minute;
}

function parseIntakeAnswers(profile) {
  const intake = profile?.intakeSurvey;
  if (!intake) return null;
  try {
    const parsed = typeof intake === 'string' ? JSON.parse(intake) : intake;
    return parsed?.answers || null;
  } catch (error) {
    return null;
  }
}

function isRegistrationCompleted(profile) {
  const intake = profile?.intakeSurvey;
  if (!intake) return false;
  try {
    const parsed = typeof intake === 'string' ? JSON.parse(intake) : intake;
    const completedFlag = parsed?.completedRegistration === true;
    const hasAnswers = parsed?.answers && typeof parsed.answers === 'object' && Object.keys(parsed.answers).length > 0;
    return completedFlag || hasAnswers;
  } catch (_error) {
    return false;
  }
}

function deriveDashboardSkillLevel(profile) {
  const answers = parseIntakeAnswers(profile);
  const rawSkill = String(profile?.skillLevel || answers?.q2 || 'acemi').toLocaleLowerCase('tr-TR');

  if (rawSkill.includes('acemi')) {
    return 'Acemi';
  }
  if (rawSkill.includes('usta şef') || rawSkill.includes('usta sef') || rawSkill.includes('master') || rawSkill.includes('expert')) {
    return 'Usta Şef';
  }
  if (rawSkill.includes('usta')) {
    return 'Usta Şef';
  }
  if (rawSkill.includes('deneyimli') || rawSkill.includes('orta')) {
    return 'Deneyimli';
  }
  return 'Acemi';
}

function hasBabyProfileEnabled(profile) {
  const answers = parseIntakeAnswers(profile);
  const rawValue =
    profile?.hasBaby ??
    profile?.bebek_var_mi ??
    profile?.babyModalCompleted ??
    profile?.babyName ??
    profile?.bebek_ismi ??
    profile?.babyAgeGroup ??
    answers?.hasBaby ??
    answers?.bebek_var_mi ??
    answers?.babyName ??
    answers?.bebek_ismi ??
    null;

  if (typeof rawValue === 'boolean') return rawValue;
  if (typeof rawValue === 'number') return rawValue > 0;
  if (typeof rawValue === 'string') {
    const normalized = rawValue.trim().toLocaleLowerCase('tr-TR');
    if (!normalized) return false;
    return ['evet', 'yes', 'true', '1', 'var'].includes(normalized) || normalized.length > 1;
  }
  return false;
}

function getNotificationMinutes(config) {
  return [
    parseTimeToMinutes(config?.breakfast?.notificationTime),
    parseTimeToMinutes(config?.breakfastReminderTime),
    parseTimeToMinutes(config?.lunch?.notificationTime),
    parseTimeToMinutes(config?.dinner?.notificationTime)
  ].filter((value) => Number.isInteger(value));
}

function hasCanonicalReminderPreference(profile, planningConfig) {
  const hasAutoMenuTiming =
    Boolean(profile?.hasSetAutoMenuTiming) ||
    Boolean(planningConfig?.hasSetAutoMenuTiming);

  const hasBreakfastTiming = Boolean(profile?.hasSetBreakfastTiming);

  if (!hasAutoMenuTiming && !hasBreakfastTiming) return false;

  const candidateMinutes = [
    parseTimeToMinutes(profile?.planning_auto_menu_time_workday),
    parseTimeToMinutes(profile?.planning_auto_menu_time_weekend),
    parseTimeToMinutes(profile?.breakfastWeekdayTime),
    parseTimeToMinutes(profile?.breakfastWeekendTime),
    parseTimeToMinutes(planningConfig?.notifications?.auto_menu_workday),
    parseTimeToMinutes(planningConfig?.notifications?.auto_menu_weekend)
  ].filter((value) => Number.isInteger(value));

  return candidateMinutes.length > 0;
}

function resolveRecipeImage(recipe) {
  const raw = recipe?.imageUrl || recipe?.image_url || recipe?.image || '';
  if (!raw) return 'https://images.unsplash.com/photo-1495195129352-aec329a6d7ca?q=80&w=800&auto=format&fit=crop';
  return raw.startsWith('http') || raw.startsWith('data:') ? raw : `${API_BASE_URL}${raw}`;
}

function getCompactPriceSource(recipe) {
  return String(recipe?.priceSnapshot?.source || '').trim().toUpperCase();
}

function hasSnapshotBreakdown(recipe) {
  return Array.isArray(recipe?.priceSnapshot?.breakdown) && recipe.priceSnapshot.breakdown.length > 0;
}

function shouldHydrateRecipeCost(recipe, displayedCostTotal) {
  const compactPriceSource = getCompactPriceSource(recipe);
  return (
    recipe?.sourceOfTruth === 'library_catalog' ||
    compactPriceSource === 'LIBRARY_CATALOG' ||
    compactPriceSource === 'SMART_SEARCH_DISH_FALLBACK' ||
    compactPriceSource === 'FALLBACK' ||
    !hasSnapshotBreakdown(recipe) ||
    Number(displayedCostTotal || 0) <= 0
  );
}

function buildDetailPath(recipe, portion) {
  const numericRecipeId = Number(recipe?.id);
  const safePortion = Number(portion) || 1;
  if (recipe?.sourceOfTruth === 'library_catalog') {
    return `/api/recipes/library/${numericRecipeId}?portion=${safePortion}`;
  }
  return `/api/recipes/${numericRecipeId}?portion=${safePortion}`;
}

function formatRecipeCost(recipe, currentPortion = null, hydratedCostMap = {}) {
  const { displayedCostTotal } = calculateRecipeCostDisplay(recipe, currentPortion);
  const hydrated = Number(hydratedCostMap?.[recipe?.id] || 0);
  const effectiveTotal = hydrated > 0 ? hydrated : Number(displayedCostTotal || 0);
  return Number(effectiveTotal || 0).toLocaleString('tr-TR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
}

function MiniTodayRecipeCard({ recipe, slotLabel, onOpen, currentPortion, hydratedCostMap }) {
  if (!recipe) return null;

  const title = recipe.title || recipe.name || 'Tarif';
  const time = recipe.cookingTime || recipe.cookTime || 25;
  const servings = recipe.presentation?.appliedPortion || recipe.targetPortion || recipe.servings || recipe.canonical?.baseServings || 4;
  const calories = recipe.calories || recipe.totalCalories || 0;

  return (
    <div className="min-h-[320px] overflow-hidden rounded-[1.25rem] border border-[#ece7e7] bg-white shadow-sm dark:border-gray-800 dark:bg-[#050505]">
      <div className="relative h-[124px] w-full overflow-hidden bg-slate-100 dark:bg-slate-900">
        <img
          src={resolveRecipeImage(recipe)}
          alt={title}
          className="h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = 'https://images.unsplash.com/photo-1495195129352-aec329a6d7ca?q=80&w=800&auto=format&fit=crop';
          }}
        />
      </div>

      <div className="p-3.5 pb-3">
        <p className="mb-2 text-[9px] font-black uppercase tracking-[0.16em] text-gray-400">{slotLabel}</p>
        <h4 className="min-h-[38px] text-[13px] font-black leading-tight text-slate-800 dark:text-white line-clamp-2">{title}</h4>

        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px] font-bold text-slate-500 dark:text-slate-400">
          <span>{time} dk</span>
          <span className="size-1 rounded-full bg-slate-300 dark:bg-slate-700"></span>
          <span>{servings} porsiyon</span>
          <span className="size-1 rounded-full bg-slate-300 dark:bg-slate-700"></span>
          <span>{Math.round(calories)} kcal</span>
        </div>

        <div className="mt-2.5 text-[11px] font-black text-slate-600 dark:text-slate-300">
          Tahmini Maliyet: <span className="text-rose-500">{formatRecipeCost(recipe, currentPortion, hydratedCostMap)} ₺</span>
        </div>


      </div>
    </div>
  );
}


function MiniTodayPairCard({ recipe, slotLabel, onOpen, currentPortion, hydratedCostMap, accent = 'bg-slate-900' }) {
  if (!recipe) return null;

  const title = recipe.title || recipe.name || 'Tarif';
  const time = recipe.cookingTime || recipe.cookTime || 25;
  const servings = recipe.presentation?.appliedPortion || recipe.targetPortion || recipe.servings || recipe.canonical?.baseServings || 4;
  const calories = recipe.calories || recipe.totalCalories || 0;

  return (
    <div className="h-full min-h-[260px] overflow-hidden rounded-[1.35rem] border border-[#ece7e7] bg-white shadow-sm dark:border-gray-800 dark:bg-[#050505]">
      <div className="relative h-[112px] w-full overflow-hidden bg-slate-100 dark:bg-slate-900">
        <img
          src={resolveRecipeImage(recipe)}
          alt={title}
          className="h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = 'https://images.unsplash.com/photo-1495195129352-aec329a6d7ca?q=80&w=800&auto=format&fit=crop';
          }}
        />
        <div className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.12em] text-white ${accent}`}>
          {slotLabel}
        </div>
      </div>

      <div className="flex min-h-[148px] flex-col p-3">
        <div className="mb-2 flex items-start justify-between gap-2">
          <h4 className="min-h-[34px] flex-1 text-[13px] font-black leading-tight text-slate-800 dark:text-white line-clamp-2">
            {title}
          </h4>
          <span className="shrink-0 rounded-full bg-rose-50 px-2.5 py-1 text-[10px] font-black text-rose-500 dark:bg-rose-500/10">
            {formatRecipeCost(recipe, currentPortion, hydratedCostMap)} ₺
          </span>
        </div>

        <div className="mb-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] font-black leading-tight text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-emerald-300">
          Bugün yap
        </div>

        <div className="flex flex-wrap items-center gap-1.5 text-[9px] font-bold text-slate-500 dark:text-slate-400">
          <span>{time} dk</span>
          <span className="size-1 rounded-full bg-slate-300 dark:bg-slate-700" />
          <span>{servings} porsiyon</span>
          <span className="size-1 rounded-full bg-slate-300 dark:bg-slate-700" />
          <span>{Math.round(calories)} kcal</span>
        </div>

        <button
          type="button"
          onClick={() => onOpen?.(recipe)}
          className="mt-auto w-full rounded-xl border border-[#ece7e7] px-3 py-2 text-[10px] font-black text-slate-500 transition hover:border-primary/30 hover:text-slate-800 dark:border-gray-800 dark:text-slate-300"
        >
          Detaylar
        </button>
      </div>
    </div>
  );
}

function MenuStackRow({ recipe, slotLabel, onOpen, accent = 'bg-rose-500', currentPortion, hydratedCostMap }) {
  if (!recipe) return null;

  const title = recipe.title || recipe.name || 'Tarif';
  const time = recipe.cookingTime || recipe.cookTime || 25;
  const calories = recipe.calories || recipe.totalCalories || 0;

  return (
    <button
      type="button"
      onClick={() => onOpen?.(recipe)}
      className="flex w-full items-stretch overflow-hidden rounded-[1.2rem] border border-[#ece7e7] bg-white text-left shadow-sm transition hover:shadow-md dark:border-gray-800 dark:bg-[#050505]"
    >
      <div className="relative h-[78px] w-[112px] shrink-0 overflow-hidden bg-slate-100 dark:bg-slate-900">
        <img
          src={resolveRecipeImage(recipe)}
          alt={title}
          className="h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = 'https://images.unsplash.com/photo-1495195129352-aec329a6d7ca?q=80&w=800&auto=format&fit=crop';
          }}
        />
        <div className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.14em] text-white ${accent}`}>
          {slotLabel}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <h4 className="truncate text-[13px] font-black text-slate-800 dark:text-white">{title}</h4>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[9px] font-bold text-slate-500 dark:text-slate-400">
            <span>{Math.round(calories)} kcal</span>
            <span className="size-1 rounded-full bg-slate-300 dark:bg-slate-700"></span>
            <span>{time} dk</span>
          </div>
        </div>
        <div className="shrink-0 rounded-full bg-rose-50 px-3 py-1.5 text-[10px] font-black text-rose-500 dark:bg-rose-500/10">
          {formatRecipeCost(recipe, currentPortion, hydratedCostMap)} ₺
        </div>
      </div>
    </button>
  );
}

function isMeaningfulInteractionTarget(target) {
  if (!target || !(target instanceof Element)) return false;
  return Boolean(
    target.closest('a, button, [role="button"], input, select, textarea, [data-meaningful="true"]')
  );
}

const OnboardingLanding = ({ isDarkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(null);
  const [userCreatedAt, setUserCreatedAt] = useState(new Date());
  const [textScale, setTextScale] = useState(1);

  const increaseScale = () => setTextScale(prev => Math.min(prev + 0.1, 1.5));
  const decreaseScale = () => setTextScale(prev => Math.max(prev - 0.1, 0.8));

  const [planningConfig, setPlanningConfig] = useState(null);
  const [alakartUpcomingNotice, setAlakartUpcomingNotice] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);
  const [todayViews, setTodayViews] = useState([]);
  const [todayMenuLoading, setTodayMenuLoading] = useState(false);
  const [activeTodayView, setActiveTodayView] = useState(null);
  const [breakfastActionPayload, setBreakfastActionPayload] = useState(null);
  const [breakfastActionLoading, setBreakfastActionLoading] = useState(false);
  const [breakfastModalOpen, setBreakfastModalOpen] = useState(false);
  const [breakfastCompletedDayKey, setBreakfastCompletedDayKey] = useState(null);
  const [selectedBreakfastRecipe, setSelectedBreakfastRecipe] = useState(null);
  const [breakfastRecipeModalOpen, setBreakfastRecipeModalOpen] = useState(false);
  const [mobilePushReminder, setMobilePushReminder] = useState(null);
  const [onboardingResolver, setOnboardingResolver] = useState(null);
  const [onboardingDayState, setOnboardingDayState] = useState(null);
  const [resolverLoading, setResolverLoading] = useState(false);
  const [meaningfulMarked, setMeaningfulMarked] = useState(false);
  const [surfaceModeOverride, setSurfaceModeOverride] = useState(null);
  const [rescueOutcome, setRescueOutcome] = useState(null);
  const [rescueOutcomeFocused, setRescueOutcomeFocused] = useState(false);
  const [day6ContextualAnswer, setDay6ContextualAnswer] = useState(null);
  const [authFailed, setAuthFailed] = useState(false);
  const [showBabyDetailModal, setShowBabyDetailModal] = useState(false);
  const [babyDetailName, setBabyDetailName] = useState('');
  const [babyDetailBirthDate, setBabyDetailBirthDate] = useState('');
  const [savingBabyDetail, setSavingBabyDetail] = useState(false);
  const [lunchPreferenceMode, setLunchPreferenceMode] = useState('light_practical_lunch');
  const [lunchServingSelection, setLunchServingSelection] = useState('family_size');
  const [savingLunchPreference, setSavingLunchPreference] = useState(false);
  const [babyDetailDeferralCount, setBabyDetailDeferralCount] = useState(0);
  const [babySignalGateReady, setBabySignalGateReady] = useState(false);
  const [babySignalLoginCount, setBabySignalLoginCount] = useState(1);
  const [babySignalHiddenUntilLogin, setBabySignalHiddenUntilLogin] = useState(0);
  const [hasWeeklyKarne, setHasWeeklyKarne] = useState(false);
  const [plannerBadgeSeen, setPlannerBadgeSeen] = useState(() => localStorage.getItem('planner_success_badge_seen') === '1');
  const currentPushDeviceKind = detectCurrentPushDeviceKind();
  const isMobilePushRuntime = currentPushDeviceKind === 'mobile_web';
  const seenSurfaceKeysRef = useRef(new Set());
  const onboardingMessageMode = useMemo(() => resolveOnboardingMessageMode(), []);
  const currentMealSlot = useMemo(() => resolveClientMealSlot(), []);
  const currentMealLabel = useMemo(() => mealSlotLabel(currentMealSlot), [currentMealSlot]);
  const dashboardSkillLevel = useMemo(() => deriveDashboardSkillLevel(userProfileData), [userProfileData]);
  const dashboardHasBabyProfile = useMemo(() => hasBabyProfileEnabled(userProfileData), [userProfileData]);
  const { isCurrentMealCompleted, refreshMenuCompletionState } = useMenuCompletionState({ mealSlot: currentMealSlot });
  const hasReminderPreference = useMemo(() => {
    return hasCanonicalReminderPreference(userProfileData, planningConfig);
  }, [userProfileData, planningConfig]);

  const todayViewsCacheKey = useMemo(() => {
    const inventoryVersion = Number(userProfileData?.inventoryVersion ?? -1);
    const userId = Number(userProfileData?.userId || userProfileData?.id || 0);
    const day = todayKey();
    return `${DASHBOARD_TODAY_CACHE_PREFIX}:${userId}:${day}:${currentMealSlot}:${inventoryVersion}:${dashboardSkillLevel || 'unknown'}`;
  }, [userProfileData, dashboardSkillLevel, currentMealSlot]);

  const babyDetailDeferStorageKey = useMemo(() => `${BABY_DETAIL_DEFER_PREFIX}:${todayKey()}`, []);
  const babySignalPromptDeferredTwice = babyDetailDeferralCount >= 2;
  const babySignalOptedOut = Boolean(onboardingDayState?.babyDetailOptOut);
  const onboardingUserId = Number(userProfileData?.userId || userProfileData?.id || 0);
  const currentDayKey = todayKey();
  const lunchPreferencePrompt = onboardingResolver?.lunchPreferencePrompt || null;
  const isLunchPreferenceGateActive = onboardingResolver?.activeSurface === 'lunch_preference';
  const lunchServingMeaningText = useMemo(() => {
    if (lunchPreferenceMode === 'extra_dinner_portion') {
      return 'Bu sayı akşam yemeğine eklenecek öğlen porsiyonu anlamına gelir.';
    }
    if (lunchPreferenceMode === 'separate_lunch_plan') {
      return 'Bu sayı bağımsız öğlen planının kişi sayısı olarak kullanılacak.';
    }
    if (lunchPreferenceMode === 'leftover_only') {
      return 'Bu sayı stok düşümü yapmaz; sadece öğlen rutininin kaç kişi civarı olduğunu anlamamı sağlar.';
    }
    return 'Bu sayı hafif/pratik öğlen önerilerinin porsiyon hedefi olarak kullanılacak.';
  }, [lunchPreferenceMode]);

  const readTodayViewsCache = useCallback(() => {
    if (!todayViewsCacheKey) return null;
    try {
      const raw = localStorage.getItem(todayViewsCacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed?.views)) return null;
      const smartOnly = parsed.views.filter((view) => view?.key === 'smart_search');
      return smartOnly.length > 0 ? smartOnly : null;
    } catch (_error) {
      return null;
    }
  }, [todayViewsCacheKey]);

  const writeTodayViewsCache = useCallback((views = []) => {
    if (!todayViewsCacheKey) return;
    try {
      localStorage.setItem(todayViewsCacheKey, JSON.stringify({
        createdAt: new Date().toISOString(),
        views
      }));
    } catch (_error) {
      // no-op
    }
  }, [todayViewsCacheKey]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(babyDetailDeferStorageKey);
      const count = Number(raw || 0);
      setBabyDetailDeferralCount(Number.isFinite(count) ? count : 0);
    } catch (_error) {
      setBabyDetailDeferralCount(0);
    }
  }, [babyDetailDeferStorageKey]);

  useEffect(() => {
    if (!onboardingUserId) return;

    const gateKey = `${BABY_SIGNAL_LOGIN_GATE_PREFIX}:${onboardingUserId}`;
    const sessionKey = `${BABY_SIGNAL_SESSION_MARK_PREFIX}:${onboardingUserId}`;

    let gate = { loginCount: 0, hiddenUntilLogin: 0 };
    try {
      const raw = localStorage.getItem(gateKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        gate = {
          loginCount: Number(parsed?.loginCount || 0),
          hiddenUntilLogin: Number(parsed?.hiddenUntilLogin || 0)
        };
      }
    } catch (_error) {
      gate = { loginCount: 0, hiddenUntilLogin: 0 };
    }

    const sessionMarked = sessionStorage.getItem(sessionKey) === '1';
    if (!sessionMarked) {
      gate.loginCount += 1;
      sessionStorage.setItem(sessionKey, '1');
    }

    setBabySignalLoginCount(Math.max(1, gate.loginCount || 1));
    setBabySignalHiddenUntilLogin(Math.max(0, gate.hiddenUntilLogin || 0));

    try {
      localStorage.setItem(gateKey, JSON.stringify(gate));
    } catch (_error) {
      // no-op
    }
    setBabySignalGateReady(true);
  }, [onboardingUserId]);

  useEffect(() => {
    if (!onboardingUserId) {
      setBabySignalGateReady(false);
    }
  }, [onboardingUserId]);

  const deferBabySignalToNextLogin = useCallback((reason = 'defer') => {
    if (!onboardingUserId) return;
    const nextLogin = Math.max(1, babySignalLoginCount) + 1;
    const gateKey = `${BABY_SIGNAL_LOGIN_GATE_PREFIX}:${onboardingUserId}`;
    const gate = {
      loginCount: Math.max(1, babySignalLoginCount),
      hiddenUntilLogin: nextLogin,
      reason,
      updatedAt: new Date().toISOString()
    };
    setBabySignalHiddenUntilLogin(nextLogin);
    try {
      localStorage.setItem(gateKey, JSON.stringify(gate));
    } catch (_error) {
      // no-op
    }
  }, [onboardingUserId, babySignalLoginCount]);

  const shouldHideBabySignalSurface = useMemo(() => {
    if (onboardingResolver?.activeSurface !== 'baby_signal') return false;
    if (babySignalPromptDeferredTwice || babySignalOptedOut) return true;
    if (dashboardHasBabyProfile) return true;
    return babySignalLoginCount < babySignalHiddenUntilLogin;
  }, [
    onboardingResolver?.activeSurface,
    babySignalPromptDeferredTwice,
    babySignalOptedOut,
    dashboardHasBabyProfile,
    babySignalLoginCount,
    babySignalHiddenUntilLogin
  ]);

  const shouldPromptBabySignalOnAnyClick = useMemo(() => {
    const onboardingDay = Number(
      onboardingResolver?.onboardingDay
      || onboardingDayState?.onboardingDay
      || 0
    );
    const completedSteps = new Set(
      Array.isArray(onboardingDayState?.completedSteps)
        ? onboardingDayState.completedSteps
        : []
    );
    const babyAlreadyAnswered = completedSteps.has('baby_signal_answered');

    return (
      onboardingDay === 1
      && !babyAlreadyAnswered
      && babySignalGateReady
      && !dashboardHasBabyProfile
      && !babySignalOptedOut
    );
  }, [
    onboardingResolver?.onboardingDay,
    onboardingDayState?.onboardingDay,
    onboardingDayState?.completedSteps,
    babySignalGateReady,
    dashboardHasBabyProfile,
    babySignalOptedOut
  ]);

  const logOnboardingEvent = useCallback(async (eventName, metadata = {}) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const resolvedMode = surfaceModeOverride || onboardingResolver?.mode || null;
    try {
      await fetch(`${API_BASE_URL}/api/profile/onboarding/event`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          event: eventName,
          surface: onboardingResolver?.activeSurface || null,
          mode: resolvedMode,
          reason: onboardingResolver?.reason || null,
          metadata
        })
      });
    } catch (_error) {
      // no-op
    }
  }, [onboardingResolver, surfaceModeOverride]);

  useEffect(() => {
    if (!shouldPromptBabySignalOnAnyClick) return;
    if (showBabyDetailModal) return;
    setShowBabyDetailModal(true);
    logOnboardingEvent('onboarding_surface_auto_prompted', {
      action: 'open_baby_detail_modal',
      source: 'dashboard_landing_auto'
    });
  }, [shouldPromptBabySignalOnAnyClick, showBabyDetailModal, logOnboardingEvent]);

  const hydrateTodayViewsForDashboard = useCallback(async (views = [], headers = {}) => {
    const uniqueIds = Array.from(
      new Set(
        views
          .flatMap((view) => collectViewRecipes(view))
          .map((recipe) => String(recipe?.id || '').trim())
          .filter(Boolean)
      )
    );

    if (!uniqueIds.length) return views;

    const detailResults = await Promise.all(
      uniqueIds.map(async (id) => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/recipes/${id}?_ts=${Date.now()}`, {
            cache: 'no-store',
            headers
          });
          if (!res.ok) return null;
          const full = await res.json();
          return [id, full];
        } catch (_error) {
          return null;
        }
      })
    );

    const recipeById = new Map(detailResults.filter(Boolean));
    if (!recipeById.size) return views;

    return views
      .map((view) => patchViewWithRecipes(view, recipeById))
      .map((view) => enforceStructuredStockGuard(view))
      .filter(Boolean);
  }, []);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('authToken');
    setAuthFailed(true);
    navigate('/giris-yap', { replace: true });
  }, [navigate]);

  const hydrateBreakfastSuggestionsForSurface = useCallback(async (suggestions = []) => {
    const list = Array.isArray(suggestions) ? suggestions : [];
    const enriched = await Promise.all(list.map(async (suggestion) => {
      if (suggestion?.imageUrl || suggestion?.image_url) return suggestion;
      try {
        const hydrated = await hydrateRecipeForModal({
          id: suggestion?.recipeId,
          recipeId: suggestion?.recipeId,
          name: suggestion?.title || 'Kahvalti Onerisi',
          title: suggestion?.title || 'Kahvalti Onerisi'
        });
        return {
          ...suggestion,
          imageUrl: hydrated?.imageUrl || hydrated?.image_url || suggestion?.imageUrl || null,
          image_url: hydrated?.image_url || hydrated?.imageUrl || suggestion?.image_url || null
        };
      } catch (_error) {
        return suggestion;
      }
    }));
    return enriched;
  }, []);

  useEffect(() => {
    setPlannerBadgeSeen(localStorage.getItem('planner_success_badge_seen') === '1');
  }, []);

  useEffect(() => {
    // Fetch planning config
    const fetchConfig = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        handleUnauthorized();
        return;
      }
      try {
        const res = await axios.get(`${API_BASE_URL}/api/planning/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          setPlanningConfig(res.data.config);
        }
        setHasWeeklyKarne(false);
        // Alakart geri sayım + alışveriş durumu (yaklaşan plan) — dashboard hatırlatıcısı
        try {
          const rr = await axios.get(`${API_BASE_URL}/api/planning/today-resolve`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const n = rr?.data?.notice;
          setAlakartUpcomingNotice(n && n.type === 'upcoming_plan' && n.planSource === 'alakart' ? n : null);
        } catch (_e) { /* resolver opsiyonel — sessiz geç */ }

        // [İnaktiflik] Dashboard açılışını işaretle; 10+ gün ara varsa sıfırlama modalını aç.
        try {
          const ds = await axios.post(`${API_BASE_URL}/api/profile/update-dashboard-shown`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (ds?.data?.wasInactive) {
            // Modalı render'ı bloklamadan, kısa gecikmeyle aç.
            setTimeout(() => { promptKitchenReset({ inactivity: true }); }, 800);
          }
        } catch (_e) { /* opsiyonel — sessiz geç */ }
      } catch (e) {
        if (e?.response?.status === 401) {
          handleUnauthorized();
          return;
        }
        console.error("Config fetch error:", e);
      }
    };
    fetchConfig();
  }, [handleUnauthorized]);


  useEffect(() => {
    if (!document.querySelector('link[data-onboarding-font]')) {
      const l = document.createElement('link');
      l.setAttribute('data-onboarding-font', 'true');
      l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@200;300;400;500;600;700;800&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          if (mounted) handleUnauthorized();
          return;
        }
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
        const res = await fetch(`${API_BASE_URL}/auth/me`, {
          credentials: 'include',
          headers
        });
        if (!mounted) return;
        if (res.status === 401) {
          handleUnauthorized();
          return;
        }
        if (res.ok) {
          const user = await res.json();
          if (user && user.name) {
            const firstName = (user.name || '').split(' ').filter(Boolean)[0];
            setDisplayName(firstName || null);
            if (user.createdAt) setUserCreatedAt(user.createdAt);
          }
        }
        try {
          const pr = await fetch(`${API_BASE_URL}/api/profile`, {
            credentials: 'include',
            headers
          });
          if (!mounted) return;
          if (pr.status === 401) {
            handleUnauthorized();
            return;
          }
          if (pr.ok) {
            const profile = await pr.json();
            if (!isRegistrationCompleted(profile)) {
              window.location.href = '/registration-survey';
              return;
            }
            const intake = profile && profile.intakeSurvey;
            let first = null;
            if (intake) {
              let obj = intake;
              if (typeof obj === 'string') obj = JSON.parse(obj);
              const ans = obj && obj.answers;
              if (ans) {
                first = ans.firstName || ans.name || ans.ad || null;
              }
            }
            if (first) setDisplayName(String(first).split(' ').filter(Boolean)[0] || null);
            setUserProfileData(profile);

            // Onboarding Check: İlk girişse Setup sayfasına yönlendir
            if (profile && profile.inventoryVersion === 0 && !localStorage.getItem('onboarding_skipped')) {
              window.location.href = '/onboarding-setup';
            }
          }
        } catch (e) { }
      } catch (err) { }
    })();
    return () => { mounted = false; };
  }, [handleUnauthorized]);

  const entitlementState = userProfileData?.entitlements || {};
  const daysSinceCreation = Math.floor((new Date() - new Date(userCreatedAt)) / (1000 * 60 * 60 * 24));
  const isLocked = entitlementState.lockedByTime ?? (daysSinceCreation < 7);
  const daysRemaining = Number(entitlementState.daysRemaining ?? (7 - daysSinceCreation));

  const [modalContent, setModalContent] = useState(null);



  const lockInfo = {
    planner: {
      title: "Mutfak Planlayıcım (Planlayın)",
      message: "Şefim; Sisteme ilk başladığınızdan itibaren 7 günlük yolculuğunuz süresince sizin, ailenizin ve mutfağınızın özellikleri bu kart altında planlanır. Bu sayede size ve sevdiklerinize sağlıklı, ekonomik, doyurucu ve Alerjan güvenlikli bir mutfak hazırlıyoruz. 7 günün sonunda bu planı açarak sistemi özelleştirebilirsiniz.",
      icon: "fa-calendar-check"
    },
    alakart: {
      title: "Alakart Menü",
      message: "Şefim; Alakart Menü sizin için 7 günlük tam kapsamlı bir plan hazırlar; her öğünü çorba, ana yemek, salata ve tatlı dengesiyle kusursuz bir menüye dönüştürür. Üstelik bu süreçte hiçbir şey düşünmenize gerek kalmaz: Planlanan yemekler için elinizde eksik olan malzemeler otomatik olarak satın alma listesine eklenir. Hem bütçenizi koruyan hem de mutfaktaki karar verme karmaşasını bitiren bu modül, stoklarınızı en verimli şekilde yemeğe dönüştürerek size modern ve sürdürülebilir bir şef deneyimi sunar.",
      icon: "fa-wand-magic-sparkles"
    },
    allergen: {
      title: "Alerjen Mutfağı",
      message: "Şefim; Alerjen Mutfağı, hassasiyeti olan aile üyeleriniz için en güvenli limanınızdır. Binlerce tarifi gelişmiş algoritmalarımızla tarayarak, içeriğinde riskli bir madde olup olmadığını anında tespit ederiz. Size ve sevdiklerinize sadece yemeğin lezzetine odaklanmak kalır.",
      icon: "fa-shield-halved"
    },
    diet: {
      title: "Diyet Mutfağı",
      message: "Şefim; Diyet Mutfağı sadece bir tarif listesi değil, profesyonel bir yol arkadaşıdır. Kişisel makrolarınıza ve hedeflerinize uygun menüler sunarken, en can alıcı özelliği ise diyetisyeninizle kurduğu köprüdür. Hazırlanan planı tek tıkla uzmanınıza link olarak gönderebilir, onunla senkronize bir şekilde çalışarak hedeflerinize emin adımlarla ilerleyebilirsiniz.",
      icon: "fa-apple-whole"
    },
    athlete: {
      title: "Sporcu Mutfağı",
      message: "Şefim; Sporcu Mutfağı, fiziksel performansınızı zirveye taşıyacak makro dengeli bir yakıttır. Antrenman yoğunluğunuza göre kas gelişimi ve toparlanma sürecinizi destekleyen bu bölümde, isterseniz antrenörünüz (PT) ile de entegre olabilirsiniz. Programınızı link üzerinden uzmanınızla paylaşıp, onun yönlendirmeleriyle profesyonel bir sporcu disiplininde mutfağınızı yönetebilirsiniz.",
      icon: "fa-dumbbell"
    },
    special_days: {
      title: "Özel Günler Mutfağı",
      message: "Şefim; Özel Günler Mutfağı doğum günü, altın günü ve özel davet sofralarını sizin için planlar. Menü, eksik malzeme, event stoğu ve davetiye akışı birlikte çalışır. Bu alan da diğer premium mutfaklar gibi 7 günlük mutfak yolculuğunuz tamamlandığında açılır.",
      icon: "fa-cake-candles"
    },
    health_plus: {
      title: "Sağlık+",
      message: "Şefim; Sağlık+ mutfak deneyimini bozmadan öneri sıralamasını size ve ailenize göre yeniden düzenler. Mode seçimi, aile bireyi bazlı güvenlik sinyalleri ve opsiyonel tahlil desteğiyle daha uygun tarifleri öne taşır.",
      icon: "fa-heart-pulse"
    }
  };

  const handleLockClick = (key) => {
    if (isLocked) {
      setModalContent(lockInfo[key]);
    }
  };

  const fetchDashboardTodayViews = useCallback(async () => {
    if (!userProfileData || !planningConfig || authFailed) return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      handleUnauthorized();
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };

    setBreakfastActionLoading(true);
    try {
      const breakfastRes = await fetch(
        `${API_BASE_URL}/api/breakfast/recommendations?intentSource=dashboard_breakfast_surface&surface=breakfast_sheet`,
        { headers }
      );
      if (breakfastRes.status === 401) {
        handleUnauthorized();
        return;
      }
      const breakfastJson = await breakfastRes.json().catch(() => ({}));
        if (breakfastRes.ok) {
          const hydratedSuggestions = await hydrateBreakfastSuggestionsForSurface(breakfastJson?.suggestions || []);
          const shouldForceBreakfastCompleted = breakfastCompletedDayKey === currentDayKey && breakfastJson?.status !== 'disabled';
          setBreakfastActionPayload({
            ...(breakfastJson || {}),
            status: shouldForceBreakfastCompleted ? 'completed' : (breakfastJson?.status || 'open'),
            suggestions: shouldForceBreakfastCompleted ? [] : hydratedSuggestions
          });
        } else {
        setBreakfastActionPayload({
          status: 'empty',
          suggestions: [],
          error: breakfastJson?.error || 'breakfast_recommendations_failed'
        });
      }
    } catch (error) {
      console.error('Dashboard breakfast action load error:', error);
      setBreakfastActionPayload({
        status: 'empty',
        suggestions: []
      });
    } finally {
      setBreakfastActionLoading(false);
    }

    if (currentMealSlot === 'breakfast') {
      setTodayViews([]);
      setActiveTodayView(null);
      if (todayViewsCacheKey) {
        try {
          localStorage.removeItem(todayViewsCacheKey);
        } catch (_error) {
          // no-op
        }
      }
      return;
    }

    // Parity-first: do not paint stale cache here; always fetch fresh Smart Search data.
    setTodayMenuLoading(true);
    try {
      const persistedPortion = Number(localStorage.getItem('userPortion') || 0);
      const currentPeriod = getCurrentPlanningPeriod();
      const planningPortion = Number(planningConfig?.[currentPeriod]?.servings || 0);
      const currentServings = persistedPortion > 0 ? persistedPortion : (planningPortion > 0 ? planningPortion : 4);
      const smartSkillLevel = localStorage.getItem('smartSearch_skillLevel') || dashboardSkillLevel;
      let resolvedPayload = null;

      // Keep parity with SmartSearchPage initialization flow first.
      try {
        const preloadRes = await fetch(`${API_BASE_URL}/api/smart-search/preload?mealSlot=${encodeURIComponent(currentMealSlot)}&intentSource=dashboard_today_preload`, {
          headers
        });
        const preloadJson = await preloadRes.json().catch(() => ({}));
        if (preloadRes.ok && preloadJson?.success) {
          const defaults = preloadJson.defaults || {};
          const initialData = preloadJson.initialData || {};
          const preloadPortion = Number(defaults.portion || currentServings || 4) || currentServings;
          const preloadSkill = defaults.skillLevel || smartSkillLevel;
          const preloadCats = Array.isArray(defaults.selectedCategories) && defaults.selectedCategories.length > 0
            ? defaults.selectedCategories
            : ['main', 'soup', 'salad'];

          if (initialData?.structuredMenu) {
            resolvedPayload = initialData;
          } else {
            const recRes = await fetch(`${API_BASE_URL}/api/smart-search/recommendations`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...headers
              },
              body: JSON.stringify({
                preferredCategories: preloadCats,
                servingSize: Number(preloadPortion) || 4,
                skillLevel: preloadSkill,
                mealSlot: currentMealSlot,
                intentSource: 'dashboard_today',
                page: 1,
                pageSize: 12
              })
            });
            const recJson = await recRes.json().catch(() => ({}));
            if (recRes.ok && recJson?.success) {
              resolvedPayload = recJson;
            }
          }
        }
      } catch {
        // Fallback to direct recommendations below.
      }

      if (!resolvedPayload) {
        const fallbackRecRes = await fetch(`${API_BASE_URL}/api/smart-search/recommendations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...headers
          },
          body: JSON.stringify({
            preferredCategories: ['main', 'soup', 'salad'],
            servingSize: currentServings,
            skillLevel: smartSkillLevel,
            mealSlot: currentMealSlot,
            intentSource: 'dashboard_today_fallback',
            page: 1,
            pageSize: 12
          })
        });
        if (fallbackRecRes.status === 401) {
          handleUnauthorized();
          return;
        }
        const fallbackRecJson = await fallbackRecRes.json().catch(() => ({}));
        if (fallbackRecRes.ok && fallbackRecJson?.success) {
          resolvedPayload = fallbackRecJson;
        }
      }

      const smartView = resolvedPayload
        ? normalizeStructuredView('smart_search', resolvedPayload)
        : null;

      const resolved = smartView ? [smartView] : [];
      setTodayViews(resolved);
      if (resolved.length > 0) {
        writeTodayViewsCache(resolved);
        hydrateTodayViewsForDashboard(resolved, headers)
          .then((hydratedViews) => {
            if (!Array.isArray(hydratedViews) || hydratedViews.length === 0) return;
            setTodayViews(hydratedViews);
            writeTodayViewsCache(hydratedViews);
          })
          .catch(() => {
            // no-op
          });
      } else if (todayViewsCacheKey) {
        localStorage.removeItem(todayViewsCacheKey);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
        return;
      }
      console.error('Dashboard today views load error:', error);
      setTodayViews([]);
    } finally {
      setTodayMenuLoading(false);
    }
  }, [
    userProfileData,
    planningConfig,
    isLocked,
    dashboardSkillLevel,
    currentMealSlot,
    authFailed,
    handleUnauthorized,
    hydrateBreakfastSuggestionsForSurface,
    hydrateTodayViewsForDashboard,
    writeTodayViewsCache,
    todayViewsCacheKey
  ]);

  useEffect(() => {
    fetchDashboardTodayViews();
  }, [fetchDashboardTodayViews]);

  useEffect(() => {
    if (!isMobilePushRuntime) {
      setMobilePushReminder(null);
      return undefined;
    }

    let cancelled = false;

    const inspectMobilePushState = async () => {
      try {
        const serverStatus = await getServerPushStatus();
        if (cancelled || !serverStatus?.ok) return;
        if (!serverStatus?.hasDesktopWebSubscription || serverStatus?.hasMobileWebSubscription) {
          setMobilePushReminder(null);
          return;
        }

        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
          setMobilePushReminder({ variant: 'needs_mobile_opt_in' });
          return;
        }

        const reg = await navigator.serviceWorker.getRegistration('/push-sw.js')
          || await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (!cancelled) {
          setMobilePushReminder(sub ? null : { variant: 'needs_mobile_opt_in' });
        }
      } catch (_error) {
        if (!cancelled) {
          setMobilePushReminder(null);
        }
      }
    };

    inspectMobilePushState();
    return () => {
      cancelled = true;
    };
  }, [isMobilePushRuntime]);

  useEffect(() => {
    if (!planningConfig || !userProfileData) return;

    const targetMinutes = getNotificationMinutes(planningConfig);
    if (!targetMinutes.length) return;

    let lastTriggeredMinute = null;

    const checkRefreshWindow = () => {
      const now = new Date();
      const minuteKey = `${todayKey()}-${now.getHours()}:${now.getMinutes()}`;
      const currentMinute = now.getHours() * 60 + now.getMinutes();
      if (targetMinutes.includes(currentMinute) && lastTriggeredMinute !== minuteKey) {
        lastTriggeredMinute = minuteKey;
        fetchDashboardTodayViews();
      }
    };

    const intervalId = window.setInterval(checkRefreshWindow, 60000);
    checkRefreshWindow();

    return () => window.clearInterval(intervalId);
  }, [planningConfig, userProfileData, fetchDashboardTodayViews]);

  useEffect(() => {
    if (breakfastCompletedDayKey && breakfastCompletedDayKey !== currentDayKey) {
      setBreakfastCompletedDayKey(null);
    }
  }, [breakfastCompletedDayKey, currentDayKey]);

  useEffect(() => {
    if (!todayViews.length) {
      setActiveTodayView(null);
      return;
    }

    if (!activeTodayView || !todayViews.some((view) => view.key === activeTodayView)) {
      setActiveTodayView(todayViews[0].key);
    }
  }, [todayViews, activeTodayView]);

  const activeDashboardView = useMemo(
    () => todayViews.find((view) => view.key === activeTodayView) || null,
    [todayViews, activeTodayView]
  );
  const breakfastSuggestions = useMemo(
    () => Array.isArray(breakfastActionPayload?.suggestions) ? breakfastActionPayload.suggestions : [],
    [breakfastActionPayload]
  );
  const isBreakfastSlotActive = currentMealSlot === 'breakfast';
  const breakfastSurfaceEnabled = breakfastActionPayload?.status !== 'disabled' && breakfastActionPayload?.blockedReason !== 'breakfast_slot_disabled';
  const isBreakfastSurface = Boolean(
    breakfastSurfaceEnabled && isBreakfastSlotActive
  );
  const breakfastDashboardVisible = Boolean(
    breakfastSurfaceEnabled && isBreakfastSlotActive && (
      breakfastActionPayload?.dashboardTriggerState?.dashboardSurfaceVisible ||
      (breakfastSuggestions.length > 0 && breakfastActionPayload?.dashboardTriggerState?.notificationId) ||
      breakfastActionPayload?.status === 'completed'
    )
  );
  const lunchPreference = userProfileData?.onboardingStates?.lunchPreference || null;
  const lunchModeInfo = useMemo(() => {
    const mode = String(
      activeDashboardView?.payloadMeta?.lunchMode
      || lunchPreference?.mode
      || ''
    ).trim();
    if (!mode || currentMealSlot !== 'lunch') return null;

    if (mode === 'light_practical_lunch') {
      return {
        tone: 'sky',
        title: 'Hafif / Pratik Öğlen Modu',
        text: 'Öğlen için ayrı büyük yemek yükü açmıyorum; hızlı ve hafif adayları öne çıkarıyorum.'
      };
    }
    if (mode === 'extra_dinner_portion') {
      const extra = lunchPreference?.extraServingSize === 'family_size'
        ? 'aile boyu'
        : `${Number(lunchPreference?.extraServingSize || 0) || ''}`.trim();
      return {
        tone: 'amber',
        title: 'Öğlen Akşamdan Çözülecek',
        text: extra
          ? `Akşam yemeği ${extra} ek porsiyonla planlanıyor. Öğlen için ayrıca menü açmıyorum.`
          : 'Akşam yemeği ek porsiyonla planlanıyor. Öğlen için ayrıca menü açmıyorum.'
      };
    }
    if (mode === 'leftover_only') {
      return {
        tone: 'slate',
        title: 'Kalanlarla Öğlen Modu',
        text: 'Öğlen için ayrı menü açmıyorum; evde kalanları değerlendirebilirsin.'
      };
    }
    if (mode === 'separate_lunch_plan') {
      return {
        tone: 'rose',
        title: 'Bağımsız Öğlen Planı',
        text: 'Bu öğlen önerileri akşam planından bağımsız üretiliyor.'
      };
    }
    return null;
  }, [activeDashboardView?.payloadMeta?.lunchMode, lunchPreference, currentMealSlot]);
  const shouldShowTodayMenuSection = !isLunchPreferenceGateActive && (!!activeDashboardView || isCurrentMealCompleted || isBreakfastSurface);
  const hasThreeCompactTodayCards = !!(
    activeDashboardView?.type === 'structured' &&
    activeDashboardView?.structuredMenu?.soup &&
    activeDashboardView?.structuredMenu?.main &&
    activeDashboardView?.structuredMenu?.salad &&
    (!Array.isArray(activeDashboardView?.structuredMenu?.pair) || activeDashboardView.structuredMenu.pair.length === 0)
  );
  const hasTodayPairings = !!(
    activeDashboardView?.type === 'structured' &&
    activeDashboardView?.structuredMenu?.main &&
    Array.isArray(activeDashboardView?.structuredMenu?.pair) &&
    activeDashboardView.structuredMenu.pair.length > 0
  );

  const currentPeriodServings = planningConfig?.[getCurrentPlanningPeriod()]?.servings || 4;
  const [hydratedTodayCostMap, setHydratedTodayCostMap] = useState({});
  const dashboardSelectableRecipes = useMemo(
    () => collectViewRecipes(activeDashboardView),
    [activeDashboardView]
  );
  const handleBreakfastRecipeOpen = useCallback(async (suggestion) => {
    const recipeId = String(suggestion?.recipeId || '').trim();
    if (!recipeId) return;
    const hydrated = await hydrateRecipeForModal({
      id: recipeId,
      recipeId,
      name: suggestion?.title || 'Kahvaltı Önerisi',
      title: suggestion?.title || 'Kahvaltı Önerisi'
    });
    setSelectedBreakfastRecipe(hydrated);
    setBreakfastRecipeModalOpen(true);
  }, []);

  const handleBreakfastRecipeMarkAsUsed = useCallback(async (recipe) => {
    const recipeId = String(recipe?.recipeId || recipe?.id || '').trim();
    if (!recipeId) return;

    const matchedSuggestion = breakfastSuggestions.find(
      (item) => String(item?.recipeId || '').trim() === recipeId
    );

    if (!matchedSuggestion || !breakfastActionPayload?.breakfastRecommendationFingerprint) {
      setBreakfastRecipeModalOpen(false);
      setSelectedBreakfastRecipe(null);
      setBreakfastModalOpen(true);
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/breakfast/consume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          recipeId: matchedSuggestion.recipeId,
          breakfastRecommendationFingerprint: breakfastActionPayload.breakfastRecommendationFingerprint,
          sourceSurface: 'dashboard_breakfast_recipe_modal',
          consumeSidePack: matchedSuggestion?.sidePack?.enabled === true,
          sidePackIngredientIds: Array.isArray(matchedSuggestion?.sidePack?.items)
            ? matchedSuggestion.sidePack.items.map((item) => item.ingredientId).filter(Boolean)
            : [],
          servingSize: Number(
            breakfastActionPayload?.requestedServingSize ||
            breakfastActionPayload?.defaultServingSize ||
            userProfileData?.planning_breakfast_servings ||
            userProfileData?.servingSize ||
            1
          ) || 1
        })
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || 'breakfast_consume_failed');
      }

      setBreakfastCompletedDayKey(currentDayKey);
      setBreakfastRecipeModalOpen(false);
      setSelectedBreakfastRecipe(null);
      setBreakfastActionPayload((prev) => ({
        ...(prev || {}),
        ...(data || {}),
        status: 'completed',
        suggestions: [],
        dashboardTriggerState: {
          ...(prev?.dashboardTriggerState || {}),
          ...(data?.dashboardTriggerState || {})
        }
      }));
      refreshMenuCompletionState?.();
      fetchDashboardTodayViews();
      await Swal.fire({
        title: 'Afiyet Olsun! 👨‍🍳',
        text: 'Kahvaltı kaydı tamamlandı ve stoklar güncellendi.',
        icon: 'success',
        confirmButtonColor: '#ff0000'
      });
    } catch (_error) {
      await Swal.fire({
        title: 'Kahvaltı kaydedilemedi',
        text: 'Lütfen tekrar deneyin.',
        icon: 'error',
        confirmButtonColor: '#ff0000'
      });
    }
  }, [
    breakfastActionPayload,
    breakfastSuggestions,
    currentDayKey,
    fetchDashboardTodayViews,
    refreshMenuCompletionState,
    userProfileData
  ]);
  useEffect(() => {
    let cancelled = false;
    const recipesInView = collectViewRecipes(activeDashboardView);
    if (!recipesInView.length) {
      setHydratedTodayCostMap({});
      return () => { cancelled = true; };
    }

    const token = localStorage.getItem('authToken');
    const nextHydrated = {};

    const tasks = recipesInView.map(async (recipe) => {
      const numericRecipeId = Number(recipe?.id);
      const { displayedCostTotal } = calculateRecipeCostDisplay(recipe, currentPeriodServings);
      if (!Number.isFinite(numericRecipeId) || !shouldHydrateRecipeCost(recipe, displayedCostTotal)) return;

      const cacheKey = `${numericRecipeId}:${Number(currentPeriodServings) || 1}`;
      if (onboardingCostHydrationCache.has(cacheKey)) {
        nextHydrated[recipe.id] = onboardingCostHydrationCache.get(cacheKey);
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}${buildDetailPath(recipe, currentPeriodServings)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!res.ok) return;
        const full = await res.json();
        const total = Number(
          full?.priceSnapshot?.total ??
          full?.presentation?.totalCost ??
          full?.totalCost ??
          0
        );
        if (Number.isFinite(total) && total > 0) {
          onboardingCostHydrationCache.set(cacheKey, total);
          nextHydrated[recipe.id] = total;
        }
      } catch {
        // Silent fallback: keep compact payload cost.
      }
    });

    Promise.all(tasks).then(() => {
      if (cancelled) return;
      setHydratedTodayCostMap(nextHydrated);
    });

    return () => {
      cancelled = true;
    };
  }, [activeDashboardView, currentPeriodServings]);

  useEffect(() => {
    if (!dashboardSelectableRecipes.length) {
      return;
    }

    const availableNames = new Set(
      dashboardSelectableRecipes
        .map((recipe) => recipe?.name || recipe?.title)
        .filter(Boolean)
    );

  }, [dashboardSelectableRecipes]);

  const fetchOnboardingResolver = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      setResolverLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/profile/onboarding/day-resolver`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setOnboardingResolver(data?.resolver || null);
      setOnboardingDayState(data?.dayState || null);
    } catch (_error) {
      // no-op
    } finally {
      setResolverLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOnboardingResolver();
  }, [fetchOnboardingResolver]);

  useEffect(() => {
    if (!isLunchPreferenceGateActive) return;

    const suggestedServingSize = lunchPreferencePrompt?.existingPreference?.selectedServingSize
      ?? lunchPreferencePrompt?.suggestedServingSize
      ?? userProfileData?.servingSize
      ?? 'family_size';

    const normalizedServing = Number.isFinite(Number(suggestedServingSize))
      ? Number(suggestedServingSize)
      : 'family_size';

    setLunchPreferenceMode(lunchPreferencePrompt?.existingPreference?.mode || 'light_practical_lunch');
    setLunchServingSelection(normalizedServing);
  }, [
    isLunchPreferenceGateActive,
    lunchPreferencePrompt?.existingPreference?.mode,
    lunchPreferencePrompt?.existingPreference?.selectedServingSize,
    lunchPreferencePrompt?.suggestedServingSize,
    userProfileData?.servingSize
  ]);

  useEffect(() => {
    const autoCompletePushBridgeIfReady = async () => {
      if (onboardingResolver?.activeSurface !== 'push_bridge') return;
      if (!hasReminderPreference) return;
      const currentSteps = Array.isArray(onboardingDayState?.completedSteps)
        ? onboardingDayState.completedSteps
        : [];
      if (currentSteps.includes('push_bridge_handled')) return;
      const token = localStorage.getItem('authToken');
      if (!token) return;
      try {
        await fetch(`${API_BASE_URL}/api/profile/onboarding/day-state`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            completedSteps: [...new Set([...currentSteps, 'push_bridge_handled'])]
          })
        });
      } catch (_error) {
        // no-op
      } finally {
        fetchOnboardingResolver();
      }
    };
    autoCompletePushBridgeIfReady();
  }, [onboardingResolver?.activeSurface, hasReminderPreference, onboardingDayState?.completedSteps, fetchOnboardingResolver]);

  useEffect(() => {
    const handleFirstMeaningfulInteraction = async (event) => {
      if (meaningfulMarked) return;
      if (!isMeaningfulInteractionTarget(event?.target)) return;
      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        setMeaningfulMarked(true);
        if (shouldPromptBabySignalOnAnyClick && !showBabyDetailModal) {
          setShowBabyDetailModal(true);
          logOnboardingEvent('onboarding_surface_any_click_prompted', {
            action: 'open_baby_detail_modal',
            source: 'dashboard_landing_first_meaningful_click'
          });
        }
        await fetch(`${API_BASE_URL}/api/profile/onboarding/meaningful-interaction`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ source: 'dashboard_landing_first_meaningful_click' })
        });
      } catch (_error) {
        // no-op
      } finally {
        fetchOnboardingResolver();
      }
    };

    document.addEventListener('click', handleFirstMeaningfulInteraction, true);
    return () => document.removeEventListener('click', handleFirstMeaningfulInteraction, true);
  }, [
    fetchOnboardingResolver,
    meaningfulMarked,
    shouldPromptBabySignalOnAnyClick,
    showBabyDetailModal,
    logOnboardingEvent
  ]);

  const onboardingSurfaceConfig = useMemo(() => {
    const surface = onboardingResolver?.activeSurface || null;
    if (!surface) return null;
    if (surface === 'push_bridge' && hasReminderPreference) return null;

    const mapping = {
      baby_signal: {
        title: 'Kısa bir soru var',
        text: 'Şefim; evinizde yemeklerinizi paylaştığınız bir bebeğimiz var mı?',
        cta: 'Hemen Yanıtla',
        href: '/smart-fridge'
      },
      push_bridge: {
        title: 'Tarif saatini tamamlayalım',
        text: 'Seçtiğin saatlerde haber verebilmemiz için son adımı tamamlayabilirsin.',
        cta: 'Ayarı Tamamla',
        href: '/notifications'
      },
      rescue: {
        title: 'Kurtarılacak ürünler var',
        text: 'Şefim; bazı ürünlerinizin SKT\'si geldiği halde kullanılmamış. Kurtarma tariflerini birlikte görelim mi?',
        cta: 'Hemen Listeyi Gör',
        href: '/notifications'
      },
      explain: {
        title: 'Dünkü önerinin nedeni hazır',
        text: pickMessageByMode(onboardingMessageMode, {
          safe: 'Dünkü öneri seçilirken bu sinyaller dikkate alındı:',
          smart: 'Dünkü öneri mutfağındaki bu sinyallere göre seçildi:',
          aggressive: 'Dünkü öneri bu güçlü sinyallere dayanarak seçildi:'
        }),
        bullets: [
          'Raf ömrü yaklaşan ürün',
          'Korunabilecek değer',
          'Hazırlama hızına uygunluk'
        ],
        cta: 'Mutfağını Gör',
        href: '/my-kitchen'
      },
      micro_control: {
        title: 'Daha bol menü için tek adım',
        text: 'Sistemin daha bol, daha kapsamlı menü ve alternatif sunması için alışveriş listesi yapmanız gerekiyor.',
        cta: 'Liste Oluştur',
        href: '/my-shopping-lists'
      },
      baby_incomplete: {
        title: 'Bebeğinizin bilgileri eksik',
        text: 'Sistemin bebeğiniz için doğru tarifler vermesi için bebeğinizin bilgilerini tamamen doldurmanız gerekir.',
        cta: 'Bilgileri Doldur',
        href: null
      },
      day7_handover: {
        title: 'Mutfağınız artık tamamen sizde',
        text: 'Şefim; sistemde 7. gününüz — artık mutfağınızı kendiniz yönetebilirsiniz. Tercihlerinizi değiştirmek için Ayarla butonunu kullanın.',
        cta: 'Ayarla',
        href: '/kitchen-planner'
      }
    };

    return mapping[surface] || null;
  }, [onboardingResolver, hasReminderPreference, onboardingMessageMode]);

  const onboardingSurfaceMode = surfaceModeOverride || onboardingResolver?.mode || null;

  useEffect(() => {
    setSurfaceModeOverride(null);
  }, [onboardingResolver?.activeSurface, onboardingResolver?.reason, onboardingResolver?.onboardingDay]);

  useEffect(() => {
    if (onboardingResolver?.activeSurface !== 'rescue') return undefined;
    if (onboardingSurfaceMode === 'chip') return undefined;

    const timer = setTimeout(() => {
      setSurfaceModeOverride('chip');
      logOnboardingEvent('onboarding_surface_auto_collapsed', { trigger: '5min_timeout' });
    }, RESCUE_BANNER_PROMINENT_MS);

    return () => clearTimeout(timer);
  }, [onboardingResolver?.activeSurface, onboardingSurfaceMode, logOnboardingEvent]);

  useEffect(() => {
    if (onboardingResolver?.activeSurface !== 'rescue') return undefined;

    const onScroll = () => {
      if (window.scrollY < RESCUE_SCROLL_COLLAPSE_PX) return;
      if ((surfaceModeOverride || onboardingResolver?.mode) === 'chip') return;
      setSurfaceModeOverride('chip');
      logOnboardingEvent('onboarding_surface_auto_collapsed', { trigger: 'scroll' });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onboardingResolver?.activeSurface, onboardingResolver?.mode, surfaceModeOverride, logOnboardingEvent]);

  const markRescueDismissedToday = useCallback(async () => {
    if (onboardingResolver?.activeSurface !== 'rescue') return;
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const nowIso = new Date().toISOString();
    const today = new Date().toISOString().slice(0, 10);
    try {
      await fetch(`${API_BASE_URL}/api/profile/onboarding/day-state`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          dismissedAt: {
            rescue: nowIso
          },
          activeAttentionLockByDay: {
            [today]: {
              activeSurface: 'rescue',
              priority: 20,
              mode: 'chip',
              reason: 'day3_rescue_dismissed_today'
            }
          }
        })
      });
    } catch (_error) {
      // no-op
    }
  }, [onboardingResolver?.activeSurface]);

  const incrementBabyDetailDeferral = useCallback(async (reason = 'later') => {
    let nextCount = babyDetailDeferralCount + 1;
    try {
      localStorage.setItem(babyDetailDeferStorageKey, String(nextCount));
    } catch (_error) {
      // no-op
    }
    setBabyDetailDeferralCount(nextCount);
    setShowBabyDetailModal(false);
    setBabyDetailName('');
    setBabyDetailBirthDate('');
    deferBabySignalToNextLogin(`baby_detail_${reason}`);
    if (reason === 'decline' && nextCount >= 2) {
      const token = localStorage.getItem('authToken');
      if (token) {
        const currentSteps = Array.isArray(onboardingDayState?.completedSteps) ? onboardingDayState.completedSteps : [];
        const mergedSteps = Array.from(new Set([...currentSteps, 'baby_detail_opt_out']));
        try {
          const res = await fetch(`${API_BASE_URL}/api/profile/onboarding/day-state`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              completedSteps: mergedSteps,
              babyDetailOptOut: true
            })
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data?.dayState) setOnboardingDayState(data.dayState);
        } catch (_error) {
          // no-op
        }
      }
    }
    await logOnboardingEvent('onboarding_baby_detail_deferred', { reason, count: nextCount });
  }, [babyDetailDeferralCount, babyDetailDeferStorageKey, logOnboardingEvent, onboardingDayState, deferBabySignalToNextLogin]);

  const handleSaveBabyDetail = useCallback(async () => {
    const babyName = String(babyDetailName || '').trim();
    const birthDate = String(babyDetailBirthDate || '').trim();
    if (!babyName || !birthDate) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      setSavingBabyDetail(true);
      const res = await fetch(`${API_BASE_URL}/api/profile/update-baby-preferences`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          hasBaby: true,
          babyName,
          babyBirthDate: birthDate,
          babyAgeGroup: computeBabyAgeGroup(birthDate),
          babyModalCompleted: true
        })
      });

      if (!res.ok) throw new Error('baby_detail_save_failed');
      const json = await res.json().catch(() => ({}));
      if (!json?.success) throw new Error('baby_detail_save_failed');

      setUserProfileData((prev) => ({
        ...(prev || {}),
        hasBaby: true,
        babyName,
        babyBirthDate: birthDate,
        babyAgeGroup: computeBabyAgeGroup(birthDate),
        babyModalCompleted: true
      }));
      window.dispatchEvent(new CustomEvent('profileUpdated'));
      setShowBabyDetailModal(false);
      setBabyDetailName('');
      setBabyDetailBirthDate('');
      setBabyDetailDeferralCount(0);
      try {
        localStorage.removeItem(babyDetailDeferStorageKey);
      } catch (_error) {
        // no-op
      }
      await logOnboardingEvent('onboarding_baby_detail_saved', { source: 'onboarding_landing_surface' });
    } catch (_error) {
      Swal.fire('Hata', 'Bebek bilgileri kaydedilemedi. Lütfen tekrar deneyin.', 'error');
    } finally {
      setSavingBabyDetail(false);
    }
  }, [babyDetailName, babyDetailBirthDate, babyDetailDeferStorageKey, logOnboardingEvent]);

  useEffect(() => {
    if (!onboardingResolver?.activeSurface) return;
    logOnboardingEvent('onboarding_surface_rendered', {
      onboardingDay: onboardingResolver?.onboardingDay || null
    });
  }, [onboardingResolver, logOnboardingEvent]);

  useEffect(() => {
    if (!onboardingResolver?.activeSurface) return undefined;
    const surface = String(onboardingResolver.activeSurface || '').trim();
    const mode = String(onboardingSurfaceMode || onboardingResolver?.mode || '').trim();
    const day = Number(onboardingResolver?.onboardingDay || 0);
    const seenKey = `${surface}:${mode}:${day}`;
    if (seenSurfaceKeysRef.current.has(seenKey)) return undefined;

    const timer = setTimeout(() => {
      seenSurfaceKeysRef.current.add(seenKey);
      logOnboardingEvent('onboarding_surface_seen', {
        onboardingDay: day || null,
        surface,
        mode
      });
    }, 1500);

    return () => clearTimeout(timer);
  }, [onboardingResolver?.activeSurface, onboardingResolver?.onboardingDay, onboardingResolver?.mode, onboardingSurfaceMode, logOnboardingEvent]);

  const shouldShowDay6ContextualPrompt = useMemo(() => {
    const isDay6 = Number(onboardingDayState?.onboardingDay || 0) === 6;
    const completed = new Set(Array.isArray(onboardingDayState?.completedSteps) ? onboardingDayState.completedSteps : []);
    return isDay6 && !completed.has('day6_contextual_portion_collected');
  }, [onboardingDayState]);

  const handleDay6ContextualAnswer = useCallback(async (portionValue) => {
    if (!shouldShowDay6ContextualPrompt) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const currentSteps = Array.isArray(onboardingDayState?.completedSteps)
      ? onboardingDayState.completedSteps
      : [];
    const mergedSteps = Array.from(new Set([...currentSteps, 'day6_contextual_portion_collected']));
    const nowIso = new Date().toISOString();
    const normalizedValue = String(portionValue || '').trim();

    try {
      await fetch(`${API_BASE_URL}/api/profile/onboarding/day-state`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          completedSteps: mergedSteps,
          lastShownAt: {
            ...(onboardingDayState?.lastShownAt || {}),
            day6_contextual_portion: nowIso
          },
          contextualData: {
            ...(onboardingDayState?.contextualData || {}),
            day6_portion_preference: normalizedValue,
            day6_portion_answered_at: nowIso
          }
        })
      });

      setDay6ContextualAnswer(normalizedValue);
      await logOnboardingEvent('onboarding_day6_contextual_answered', { portion: normalizedValue });
    } catch (_error) {
      // no-op
    } finally {
      fetchOnboardingResolver();
    }
  }, [shouldShowDay6ContextualPrompt, onboardingDayState, logOnboardingEvent, fetchOnboardingResolver]);

  const handleMicroControlDecision = useCallback(async (decision) => {
    if (onboardingResolver?.activeSurface !== 'micro_control') return;
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const currentSteps = Array.isArray(onboardingDayState?.completedSteps)
      ? onboardingDayState.completedSteps
      : [];
    const mergedSteps = Array.from(new Set([...currentSteps, 'day5_micro_control_done']));
    const nowIso = new Date().toISOString();

    try {
      await fetch(`${API_BASE_URL}/api/profile/onboarding/day-state`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          completedSteps: mergedSteps,
          dismissedAt: {
            ...(onboardingDayState?.dismissedAt || {}),
            micro_control: nowIso
          }
        })
      });
      await logOnboardingEvent('onboarding_micro_control_answered', { decision });
    } catch (_error) {
      // no-op
    } finally {
      fetchOnboardingResolver();
    }
  }, [onboardingResolver?.activeSurface, onboardingDayState, fetchOnboardingResolver, logOnboardingEvent]);

  const handleSaveLunchPreference = useCallback(async () => {
    if (!isLunchPreferenceGateActive || savingLunchPreference) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      setSavingLunchPreference(true);
      const payload = {
        mode: lunchPreferenceMode,
        selectedServingSize: lunchServingSelection,
        extraServingSize: lunchPreferenceMode === 'extra_dinner_portion' ? lunchServingSelection : null,
        source: 'day2_lunch_preference_prompt',
        canChangeLater: true,
        lunchDecisionTrace: {
          mode: lunchPreferenceMode,
          reason: 'user_selected_lunch_notification_time_and_answered_day2_prompt',
          candidateSource: lunchPreferenceMode === 'light_practical_lunch' ? 'service_type=yan_yemek' : null,
          dinnerDuplicationPrevented: lunchPreferenceMode !== 'extra_dinner_portion',
          servingSize: lunchServingSelection
        }
      };

      const res = await fetch(`${API_BASE_URL}/api/profile/onboarding/lunch-preference`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || 'lunch_preference_save_failed');
      }

      await logOnboardingEvent('onboarding_lunch_preference_answered', {
        lunchMode: lunchPreferenceMode,
        selectedServingSize: lunchServingSelection
      });
      fetchOnboardingResolver();
    } catch (_error) {
      Swal.fire('Hata', 'Öğlen tercihi kaydedilemedi. Lütfen tekrar deneyin.', 'error');
    } finally {
      setSavingLunchPreference(false);
    }
  }, [
    isLunchPreferenceGateActive,
    savingLunchPreference,
    lunchPreferenceMode,
    lunchServingSelection,
    logOnboardingEvent,
    fetchOnboardingResolver
  ]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('onboarding_rescue_outcome');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      setRescueOutcome(parsed);
      sessionStorage.removeItem('onboarding_rescue_outcome');
      logOnboardingEvent('onboarding_rescue_outcome_rendered', {
        sourceItemName: parsed.sourceItemName || null,
        transformedItemName: parsed.transformedItemName || null
      });
    } catch (_error) {
      // no-op
    }
  }, [logOnboardingEvent]);

  const handleRecipeClick = async (recipe) => {
    if (
      onboardingResolver?.activeSurface === 'baby_signal'
      && !dashboardHasBabyProfile
      && !babySignalOptedOut
    ) {
      deferBabySignalToNextLogin('recipe_click_without_baby_signal');
    }
    if (shouldShowDay6ContextualPrompt) {
      logOnboardingEvent('onboarding_day6_contextual_prompted', {
        recipeId: recipe?.id || null
      });
    }
    navigate('/smart-fridge', {
      state: {
        source: 'onboarding_landing_today_kitchen',
        recipeId: recipe?.id || null
      }
    });
  };

  return (
    <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
      <div className="flex flex-col gap-8">
        <header className="flex items-center justify-between gap-4 flex-wrap md:items-end md:justify-between">
          <div className="flex flex-col gap-2 flex-1">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                Hoş geldin, <span className="text-primary">{displayName ? `${displayName} ` : ''}</span> <span className="text-gray-900 dark:text-white">Şef!</span>🤗
              </h2>

              {/* MOBILE ONLY COMPACT BUTTONS - Fixed to top right of the welcome text section */}
              <div className="md:hidden flex shrink-0 items-center gap-1.5 bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm p-1 rounded-xl border border-[#e6dbdb] dark:border-gray-700 shadow-sm mt-1">
                <button
                  onClick={decreaseScale}
                  title="Yazıları Küçült"
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 active:scale-90"
                >
                  <span className="text-[10px] font-bold font-mono">A-</span>
                </button>
                <div className="w-px h-3 bg-gray-300 dark:bg-gray-600"></div>
                <button
                  onClick={increaseScale}
                  title="Yazıları Büyüt"
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 active:scale-90"
                >
                  <span className="text-xs font-bold font-mono">A+</span>
                </button>
              </div>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-base md:text-lg">Bugün ne yemek yapmak istersin?</p>
          </div>

          {/* DESKTOP ONLY REGULAR CONTROLS */}
          <div className="hidden md:flex items-center gap-3 bg-white/50 dark:bg-[#000]/50 backdrop-blur-md p-2 rounded-2xl border border-[#e6dbdb] dark:border-gray-800 shadow-sm transition-all hover:shadow-md">
            <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest px-2">Metin Boyutu</span>
            <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
              <button
                onClick={decreaseScale}
                title="Yazıları Küçült"
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:text-red-500 transition-all shadow-sm border border-gray-200 dark:border-gray-600 active:scale-90"
              >
                <span className="text-xs font-bold font-mono">A-</span>
              </button>
              <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>
              <button
                onClick={increaseScale}
                title="Yazıları Büyüt"
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:text-green-500 transition-all shadow-sm border border-gray-200 dark:border-gray-600 active:scale-90"
              >
                <span className="text-base font-bold font-mono">A+</span>
              </button>
            </div>
          </div>
        </header>

        {/* Alakart geri sayım + alışveriş durumu — üst bölge (Hoş geldin'in hemen altı) */}
        {alakartUpcomingNotice && (
          <AlakartCountdownCard
            notice={alakartUpcomingNotice}
            onGoShopping={(listId) => navigate(listId ? `/my-shopping-lists?list=${listId}` : '/my-shopping-lists')}
            onGoPlan={() => navigate('/alakart-menu')}
          />
        )}

        {rescueOutcome && (
          <section className="rounded-2xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-gray-900 dark:text-white">
                  {`${rescueOutcome.sourceItemName || 'Ürün'} değerlendirildi 👍`}
                </p>
                <p className="text-xs text-gray-700 dark:text-gray-300">
                  {`+${Number(rescueOutcome.daysSaved || 0)} gün raf ömrü kazanıldı`}
                </p>
                <p className="text-xs text-gray-700 dark:text-gray-300">
                  {`~${Number(rescueOutcome.valueSavedTry || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₺ israf önlendi`}
                </p>
                {rescueOutcomeFocused && (
                  <p className="mt-1 text-[11px] font-bold text-emerald-700 dark:text-emerald-300">
                    Az önce değerlendirdiğimiz ürün burada:
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setRescueOutcomeFocused(true);
                    window.dispatchEvent(new CustomEvent('dashboard:focus-kpi', {
                      detail: { type: 'tasarruf' }
                    }));
                    logOnboardingEvent('onboarding_rescue_detail_view_clicked');
                  }}
                  className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white"
                >
                  Detayı Gör
                </button>
                <button
                  type="button"
                  onClick={() => setRescueOutcome(null)}
                  className="rounded-lg border border-[#e6dbdb] bg-white px-3 py-2 text-xs font-bold text-slate-700 dark:border-gray-700 dark:bg-[#0b0b0b] dark:text-slate-200"
                >
                  Kapat
                </button>
              </div>
            </div>
          </section>
        )}

        {!resolverLoading
          && onboardingSurfaceConfig
          && !(onboardingResolver?.activeSurface === 'baby_signal' && !babySignalGateReady)
          && !shouldHideBabySignalSurface
          /* Alakart geri sayım kartı varsa düşük öncelikli nudge'ları bastır (kritik olanlar kalır) */
          && !(alakartUpcomingNotice && ['micro_control', 'explain', 'rescue'].includes(onboardingResolver?.activeSurface)) && (
            <section
              className={`rounded-2xl border ${onboardingSurfaceMode === 'chip'
                ? 'border-amber-200 bg-amber-50/80 px-4 py-2 dark:border-amber-900/40 dark:bg-amber-950/20'
                : 'border-rose-200 bg-rose-50/80 px-4 py-3 dark:border-rose-900/40 dark:bg-rose-950/20'
                }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-gray-900 dark:text-white">{onboardingSurfaceConfig.title}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">{onboardingSurfaceConfig.text}</p>
                  {Array.isArray(onboardingSurfaceConfig.bullets) && onboardingSurfaceConfig.bullets.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-[11px] font-medium text-gray-700 dark:text-gray-300">
                      {onboardingSurfaceConfig.bullets.map((bullet) => (
                        <li key={bullet}>• {bullet}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {onboardingResolver?.activeSurface === 'rescue' && onboardingSurfaceMode !== 'chip' && (
                    <button
                      type="button"
                      onClick={async () => {
                        setSurfaceModeOverride('chip');
                        await markRescueDismissedToday();
                        logOnboardingEvent('onboarding_surface_dismissed', {
                          action: 'simdi_degil'
                        });
                        logOnboardingEvent('onboarding_surface_ignored', {
                          onboardingDay: onboardingResolver?.onboardingDay || null,
                          surface: onboardingResolver?.activeSurface || null,
                          mode: onboardingSurfaceMode || onboardingResolver?.mode || null,
                          action: 'simdi_degil'
                        });
                      }}
                      className="rounded-lg border border-[#e6dbdb] bg-white px-3 py-2 text-xs font-bold text-slate-700 dark:border-gray-700 dark:bg-[#0b0b0b] dark:text-slate-200"
                    >
                      Şimdi Değil
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const surface = onboardingResolver?.activeSurface;
                      // Bebek (sinyal veya eksik) → bebek detay modalı
                      if (surface === 'baby_signal' || surface === 'baby_incomplete') {
                        setShowBabyDetailModal(true);
                        logOnboardingEvent('onboarding_surface_cta_clicked', { action: 'open_baby_detail_modal' });
                        return;
                      }
                      // Kurtarma → Bildirimler'e git ve kurtarma modalını otomatik aç (aynı modal, sıfır risk)
                      if (surface === 'rescue') {
                        logOnboardingEvent('onboarding_surface_cta_clicked', { action: 'open_rescue_modal' });
                        navigate('/notifications', { state: { autoOpenRescue: true } });
                        return;
                      }
                      logOnboardingEvent('onboarding_surface_cta_clicked', { href: onboardingSurfaceConfig.href });
                      navigate(onboardingSurfaceConfig.href);
                    }}
                    className="rounded-lg bg-primary px-3 py-2 text-xs font-bold text-white"
                  >
                    {onboardingSurfaceConfig.cta}
                  </button>
                </div>
              </div>
            </section>
          )}

        {/* [Kullanım Kılavuzu] Admin mavi ipucu carousel — sadece onboarding nudge boşken */}
        {!onboardingSurfaceConfig && (
          <SystemNudgeCarousel pageKey="dashboard" className="mb-2" />
        )}

        {mobilePushReminder?.variant === 'needs_mobile_opt_in' && (
          <section className="rounded-[1.8rem] border border-sky-200 bg-sky-50 px-5 py-4 shadow-sm dark:border-sky-900/30 dark:bg-sky-900/10">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-sky-600 dark:text-sky-300">Mobil Web Push</p>
                <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-white">
                  Bu hesap masaustunde bildirim aliyor. Telefonda da bildirim almak icin bu cihazda web push iznini acman gerekiyor.
                </p>
              </div>
              <a
                href="/notifications"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-500 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-white shadow-lg shadow-sky-500/20 transition hover:bg-sky-600"
              >
                <span className="material-symbols-outlined text-[18px]">notifications_active</span>
                Telefonda Bildirimi Ac
              </a>
            </div>
          </section>
        )}

        {lunchModeInfo && (
          <section className={`rounded-[1.6rem] border px-5 py-4 shadow-sm ${
            lunchModeInfo.tone === 'sky'
              ? 'border-sky-200 bg-sky-50 dark:border-sky-900/30 dark:bg-sky-900/10'
              : lunchModeInfo.tone === 'amber'
                ? 'border-amber-200 bg-amber-50 dark:border-amber-900/30 dark:bg-amber-900/10'
                : lunchModeInfo.tone === 'rose'
                  ? 'border-rose-200 bg-rose-50 dark:border-rose-900/30 dark:bg-rose-900/10'
                  : 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/30'
          }`}>
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-white/80 text-slate-700 dark:bg-black/20 dark:text-slate-200">
                <span className="material-symbols-outlined text-[20px]">lunch_dining</span>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500 dark:text-slate-300">Öğlen Modu</p>
                <p className="mt-1 text-sm font-black text-slate-900 dark:text-white">{lunchModeInfo.title}</p>
                <p className="mt-1 text-xs font-medium text-slate-600 dark:text-slate-300">{lunchModeInfo.text}</p>
              </div>
            </div>
          </section>
        )}

        {shouldShowTodayMenuSection && (
          <section className="relative overflow-visible rounded-[1.8rem] border border-[#e6dbdb] bg-white px-4 pb-10 pt-8 shadow-sm md:px-5 dark:border-gray-800 dark:bg-[#000]">
            <div className="flex flex-col gap-4">
              <>
                <div className="space-y-2">
                  <div className="absolute -top-3 left-5 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-rose-500 shadow-sm dark:border-rose-900/40 dark:bg-[#000] dark:text-rose-400">
                    <span className="material-symbols-outlined text-sm">restaurant_menu</span>
                    {isBreakfastSurface ? 'Günün Mutfağı - (Kahvaltı Akışı)' : getTodayViewHeadline(activeDashboardView)}
                  </div>
                  <p className="text-sm font-black text-gray-900 dark:text-white">
                    {isBreakfastSurface
                      ? 'Şefim, kahvaltınızı hazırlıyoruz.'
                      : isCurrentMealCompleted
                        ? `Şefim, afiyet olsun. ${currentMealLabel} öğününü tamamladınız.`
                        : 'Şefim; mutfağınızdaki malzemelere ve yemek alışkanlıklarınıza göre bugün sizin ve aileniz için seçtiklerimiz.'}
                  </p>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {isBreakfastSurface
                      ? 'Sabah kahvaltı önerilerini buradan da açabilir, aynı modal üzerinden seçip Yedim diyebilirsin.'
                      : isCurrentMealCompleted
                        ? 'Bugün bu slot için pişirme tamamlandı. İstersen günün diğer öğünleri için yeniden öneri alabilirsin.'
                        : activeDashboardView.meta.description}
                  </p>
                </div>

                {isBreakfastSurface && !breakfastDashboardVisible && (
                  <MenuCompletionBanner
                    isDarkMode={isDarkMode}
                    title="Şefim, Güzel bir gün geçirmenizi diliyorum."
                    subtitle="Günün mutfağı sizin için seçilen menülerin listendiği alandır."
                    detail="Seçmiş olduğunuz saatlerde, menüleriniz bu alanda hızlıca listeleniyor olacak."
                  />
                )}

                {isBreakfastSurface && breakfastActionPayload?.status === 'completed' && (
                  <MenuCompletionBanner
                    isDarkMode={isDarkMode}
                    subtitle="Bugünkü kahvaltını tamamladın."
                    detail="Öğle ve akşam için günlük aksiyon burada devam edecek."
                  />
                )}

                {isBreakfastSurface && breakfastDashboardVisible && breakfastActionPayload?.status !== 'completed' && (
                  breakfastActionLoading ? (
                    <div className="flex justify-center py-10">
                      <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-amber-500" />
                    </div>
                  ) : breakfastSuggestions.length > 0 ? (
                    <>
                      <div className="grid gap-4 lg:grid-cols-3">
                        {breakfastSuggestions.map((suggestion, index) => {
                          const recipeCardModel = {
                            id: suggestion.recipeId,
                            recipeId: suggestion.recipeId,
                            name: suggestion.title,
                            title: suggestion.title,
                            imageUrl: suggestion.imageUrl || suggestion.image_url || '',
                            image_url: suggestion.image_url || suggestion.imageUrl || '',
                            suggestedPairings: []
                          };

                          return (
                            <div key={`${suggestion.recipeId || index}`} className="space-y-3">
                              <div className="px-2">
                                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-600 dark:text-amber-300">Kahvaltı {index + 1}</p>
                              </div>
                              <RecipeMenuGroup
                                recipe={recipeCardModel}
                                isDarkMode={isDarkMode}
                                onCardClick={() => handleBreakfastRecipeOpen(suggestion)}
                                selectedRecipeNames={[]}
                                isCompleted={false}
                                userProfile={userProfileData}
                                userPortion={breakfastActionPayload?.requestedServingSize || breakfastActionPayload?.defaultServingSize || 1}
                                isModern={true}
                                hideActionButtons={true}
                                layoutMode="featured"
                              />
                              <div className="flex flex-wrap gap-2 px-2">
                                {(Array.isArray(suggestion.sidePack?.items) ? suggestion.sidePack.items : []).slice(0, 4).map((item) => (
                                  <span
                                    key={`${suggestion.recipeId}-${item.ingredientId}`}
                                    className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    {item.title}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-5 flex justify-center">
                        <button
                          type="button"
                          onClick={() => setBreakfastModalOpen(true)}
                          className="inline-flex items-center gap-2 rounded-2xl bg-amber-500 px-5 py-3 text-sm font-black text-white shadow-lg shadow-amber-500/20 transition hover:bg-amber-600"
                        >
                          <span className="material-symbols-outlined text-[18px]">breakfast_dining</span>
                          Kahvaltı Akışını Aç
                        </button>
                      </div>
                    </>
                  ) : (
                    <MenuCompletionBanner
                      isDarkMode={isDarkMode}
                      title="Şefim, afiyet olsun"
                      subtitle="Kahvaltı önerileri ayrı akışta hazırlanıyor."
                      detail="Kahvaltı önerileri hazır olduğunda buradan açıp aynı modal üzerinden ilerleyebilirsin."
                    />
                  )
                )}

                {!isBreakfastSurface && isCurrentMealCompleted && (
                  <MenuCompletionBanner
                    isDarkMode={isDarkMode}
                    subtitle={`${currentMealLabel} öğününü tamamladık.`}
                    detail="Diğer öğünler bildirim yapılmasını istediğin saatte hazırlanacak."
                  />
                )}

                {!isBreakfastSurface && !isCurrentMealCompleted && activeDashboardView.type === 'structured' && (
                  (() => {
                    const pairings = Array.isArray(activeDashboardView.structuredMenu?.pair)
                      ? activeDashboardView.structuredMenu.pair.filter(Boolean)
                      : [];
                    const hasPairings = pairings.length > 0;
                    return (
                      <div className={`grid grid-cols-1 md:grid-cols-2 ${hasPairings ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 ${isCurrentMealCompleted ? 'pointer-events-none' : ''}`}>
                        {(() => {
                          const slots = [
                            { id: 'soup', label: 'ÇORBA', recipe: activeDashboardView.structuredMenu?.soup, layoutMode: 'compact' },
                            {
                              id: 'main',
                              label: hasPairings ? 'ANA YEMEK + EŞLİKÇİ' : 'ANA YEMEK',
                              recipe: activeDashboardView.structuredMenu?.main
                                ? { ...activeDashboardView.structuredMenu.main, suggestedPairings: pairings }
                                : null,
                              layoutMode: hasPairings ? 'featured' : 'compact'
                            },
                            { id: 'salad', label: 'SALATA', recipe: activeDashboardView.structuredMenu?.salad, layoutMode: 'compact' }
                          ].filter((slot) => slot.recipe);

                          return slots.map((slot) => (
                            <div
                              key={`${activeDashboardView.key}-${slot.id}`}
                              className={`${slot.id === 'main' && hasPairings ? 'lg:col-span-2' : 'lg:col-span-1'} flex flex-col h-full`}
                            >
                              <div className="mb-3 px-2 flex-none">
                                <span className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-black rounded-lg uppercase tracking-widest border border-slate-200 dark:border-slate-700">
                                  {slot.label}
                                </span>
                              </div>
                              <div className="flex-1">
                                <RecipeMenuGroup
                                  recipe={slot.recipe}
                                  isDarkMode={isDarkMode}
                                  onCardClick={handleRecipeClick}
                                  selectedRecipeNames={[]}
                                  isCompleted={false}
                                  userProfile={userProfileData}
                                  userPortion={currentPeriodServings}
                                  isModern={true}
                                  hideActionButtons={true}
                                  layoutMode={slot.layoutMode}
                                />
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    );
                  })()
                )}

                {!isBreakfastSurface && !isCurrentMealCompleted && activeDashboardView.type === 'meals' && (
                  <div className={`grid gap-4 lg:grid-cols-3 ${isCurrentMealCompleted ? 'pointer-events-none opacity-60 grayscale-[0.45] saturate-50' : ''}`}>
                    {activeDashboardView.meals.map((meal) => {
                      const mealLabelMap = {
                        breakfast: 'Kahvaltı',
                        lunch: 'Öğle',
                        dinner: 'Akşam'
                      };

                      return (
                        <div
                          key={`${activeDashboardView.key}-${meal.mealType}`}
                          className="rounded-[1.5rem] border border-[#e6dbdb] bg-background-light/70 p-4 dark:border-gray-800 dark:bg-[#111]"
                        >
                          <p className="mb-3 text-[11px] font-black uppercase tracking-[0.18em] text-gray-400">
                            {mealLabelMap[meal.mealType] || meal.mealType}
                          </p>
                          <div className="space-y-3">
                            {meal.recipes.map((recipe) => (
                              <MiniTodayRecipeCard
                                key={`${activeDashboardView.key}-${meal.mealType}-${recipe.id || recipe.name}`}
                                recipe={recipe}
                                slotLabel={mealLabelMap[meal.mealType] || meal.mealType}
                                onOpen={handleRecipeClick}
                                currentPortion={currentPeriodServings}
                                hydratedCostMap={hydratedTodayCostMap}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!isBreakfastSurface && isCurrentMealCompleted ? (
                  <div className="mt-8 flex justify-center">
                    <p className="text-sm font-black text-slate-500 italic">Şefim, afiyet olsun. Günün diğer öğünleri için yeniden öneri alabilirsin.</p>
                  </div>
                ) : null}
              </>

            </div>
          </section>
        )}



        {/* Advertisement Carousel - Area 1 */}
        <section className="w-full">
          <AdCarousel
            areaId={1}
            className="w-full"
            onAdClick={(ad) => console.log('Ad clicked:', ad)}
          />
        </section>

        <IntelligenceBanner isDarkMode={isDarkMode} />

        {/* Quick Access Section */}
        <section className="bg-white py-8 dark:bg-[#1a1a1a]">
          <div className="mx-auto max-w-lg mb-8">
            <div className="flex items-center justify-center">
              <div className="h-px flex-grow bg-[#e6dbdb] dark:bg-gray-700"></div>
              <h3 id="ozellikler" className="text-3xl font-black tracking-tight text-gray-900 dark:text-white sm:text-2xl px-4 shrink-0">Hızlı Erişim</h3>
              <div className="h-px flex-grow bg-[#e6dbdb] dark:bg-gray-700"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <a href="/quick-search" className="group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:border-gray-800 dark:bg-[#000] dark:hover:border-primary/30 block cursor-pointer overflow-hidden flex">
              <div className="w-1/3 flex-shrink-0">
                <img src={HizliAra} alt="Hızlı Tarif Ara" className="h-full w-full object-contain" />
              </div>
              <div className="w-2/3 p-6 flex flex-col justify-center">
                <div className="flex items-center mb-3">
                  <i className="fa-solid fa-search text-xl text-primary mr-3"></i>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Hızlı Tarif Ara</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>Elinizdeki malzemeleri girin, onlarla yapabileceğiniz tarifleri anında keşfedin.</p>
              </div>
            </a>

            <a href="/today-menu" className="group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:border-gray-800 dark:bg-[#000] dark:hover:border-primary/30 block cursor-pointer overflow-hidden flex">
              <div className="w-1/3 flex-shrink-0">
                <img src={GununMenusu} alt="Günün Menüsü" className="h-full w-full object-contain" />
              </div>
              <div className="w-2/3 p-6 flex flex-col justify-center">
                <div className="flex items-center mb-3">
                  <i className="fa-solid fa-calendar text-xl text-primary mr-3"></i>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Günün Menüsü</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>
                  {activeDashboardView
                    ? `${activeDashboardView.meta.label} kaynağından bugünkü önerileri görüntüleyin.`
                    : 'Bugün için hazırlanan günlük öneri setini buradan açın.'}
                </p>
              </div>
            </a>

            <a href="/smart-fridge" className="group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:border-gray-800 dark:bg-[#000] dark:hover:border-primary/30 block cursor-pointer overflow-hidden flex">
              <div className="w-1/3 flex-shrink-0">
                <img src={DolapSef} alt="Buzdolabımda Ara" className="h-full w-full object-contain" />
              </div>
              <div className="w-2/3 p-6 flex flex-col justify-center">
                <div className="flex items-center mb-3">
                  <i className="fa-solid fa-search text-xl text-primary mr-3"></i>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Buzdolabımda Ara</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>Dolabınızdaki malzemeleri girin, israfı önleyecek yaratıcı tarifler alın.</p>
              </div>
            </a>

            <a href="/my-kitchen" className="group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:border-gray-800 dark:bg-[#000] dark:hover:border-primary/30 block cursor-pointer overflow-hidden flex">
              <div className="w-1/3 flex-shrink-0">
                <img src={BuzdolabimdaAra} alt="Mutfağım" className="h-full w-full object-contain" />
              </div>
              <div className="w-2/3 p-6 flex flex-col justify-center">
                <div className="flex items-center mb-3">
                  <i className="fa-solid fa-kitchen-set text-xl text-primary mr-3"></i>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Mutfağım</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>Evinizde ki tüm mutfak malzemelerinizin STK, paket ve bulunduğu ortamı raflara kadar listeleyen ve fiyatlarını görünür kılan mutfağınızın dijital ikizidir.</p>
              </div>
            </a>

            <a href="/my-favorites" className="group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:border-gray-800 dark:bg-[#000] dark:hover:border-primary/30 block cursor-pointer overflow-hidden flex">
              <div className="w-1/3 flex-shrink-0">
                <img src={YaptigimTarifler} alt="Yaptığım Tarifler" className="h-full w-full object-contain" />
              </div>
              <div className="w-2/3 p-6 flex flex-col justify-center">
                <div className="flex items-center mb-3">
                  <i className="fa-solid fa-heart text-xl text-primary mr-3"></i>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Favorilerim</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>Pişirdiğiniz yemekleri işaretleyin, mutfak yolculuğunuzu kayıt altına alın.</p>
              </div>
            </a>

            <div
              onClick={() => {
                if (isLocked) {
                  handleLockClick('planner');
                  return;
                }
                window.location.href = hasWeeklyKarne ? "/kitchen-planner?tab=report" : "/kitchen-planner";
              }}
              className={`group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all dark:border-gray-800 dark:bg-[#000] overflow-hidden flex ${isLocked ? 'opacity-70 cursor-not-allowed' : 'hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:hover:border-primary/30 cursor-pointer'}`}
            >
              {isLocked && (
                <div className="absolute top-2 right-2 z-10">
                  <span className="bg-amber-500/90 text-[9px] font-black text-white px-2 py-1 rounded-full shadow-sm uppercase tracking-tight">
                    {daysRemaining} Gün Sonra
                  </span>
                </div>
              )}
              {!isLocked && hasWeeklyKarne && !plannerBadgeSeen && (
                <div className="absolute top-3 right-3 z-10">
                  <span className="rounded-full bg-red-600 px-3 py-1 text-[10px] font-black uppercase tracking-wide text-white shadow-sm">
                    Başarı Grafiği
                  </span>
                </div>
              )}
              <div className="w-1/3 flex-shrink-0">
                <img src={PlanlaImg} alt="Planla" className="h-full w-full object-contain" />
              </div>
              <div className="w-2/3 p-6 flex flex-col justify-center">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <i className="fa-solid fa-clock text-xl text-primary mr-3"></i>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">Planlayın</h3>
                  </div>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>
                  {isLocked
                    ? `Bu özellik ${daysRemaining} gün sonra sizin ve aileniz için aktif olacak.`
                    : 'Malzemelerinizi, porsiyon sayısını ve kalori hedefinizi belirleyin.'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Advertisement Carousel - Area 2 */}
        <section className="w-full">
          <AdCarousel
            areaId={2}
            className="w-full"
            onAdClick={(ad) => console.log('Ad Area 2 clicked:', ad)}
          />
        </section>

        {/* Premium Kitchens Section */}
        <section className="bg-white py-8 dark:bg-[#1a1a1a]">
          <div className="mx-auto max-w-lg mb-8">
            <div className="flex items-center justify-center">
              <div className="h-px flex-grow bg-[#e6dbdb] dark:bg-gray-700"></div>
              <h3 className="text-3xl font-black tracking-tight text-gray-900 dark:text-white sm:text-2xl px-4 shrink-0">Premium Mutfaklar</h3>
              <div className="h-px flex-grow bg-[#e6dbdb] dark:bg-gray-700"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {/* 1. Alerjen Mutfağı */}
            <a
              href={isLocked ? "#" : "/allergen-kitchen"}
              onClick={(e) => {
                if (isLocked) {
                  e.preventDefault();
                  handleLockClick('allergen');
                }
              }}
              className={`group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all dark:border-gray-800 dark:bg-[#000] block overflow-hidden flex items-stretch min-h-[160px] ${!isLocked ? 'hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:hover:border-primary/30 cursor-pointer' : 'opacity-60 grayscale-[0.8] cursor-not-allowed'
                }`}
            >
              {isLocked && (
                <div className="absolute -bottom-6 -right-6 z-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none">
                  <i className="fa-solid fa-lock text-[140px] rotate-12 text-gray-900 dark:text-white"></i>
                </div>
              )}
              <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                <span className="bg-[#cc2b1e] text-[8px] font-black text-white px-1.5 py-0.5 rounded shadow-sm tracking-tighter shadow-red-900/40">PREMIUM</span>
                {isLocked && (
                  <span className="bg-amber-500/90 text-[7px] font-bold text-white px-1.5 py-0.5 rounded shadow-sm uppercase tracking-tighter">
                    {daysRemaining} GÜN SONRA
                  </span>
                )}
              </div>
              <div className="w-1/3 flex items-center justify-center p-2">
                <img src={AllergenImg} alt="Alerjen Mutfağı" className="max-h-[110px] w-full object-contain transform group-hover:scale-110 transition-transform duration-300" />
              </div>
              <div className="w-2/3 p-5 flex flex-col justify-center">
                <div className="flex items-center mb-2">
                  <i className="fa-solid fa-shield-halved text-lg text-primary mr-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Alerjen Mutfağı</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>
                  {isLocked ? `Bu özellik ${daysRemaining} gün sonra sizin ve aileniz için aktif olacak` : 'Güvenli içerik taraması ve alerjen uyarıları.'}
                </p>
              </div>
            </a>

            {/* 2. Alakart Menü */}
            <a
              href={isLocked ? "#" : "/alakart-menu"}
              onClick={(e) => {
                if (isLocked) {
                  e.preventDefault();
                  handleLockClick('alakart');
                }
              }}
              className={`group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all dark:border-gray-800 dark:bg-[#000] block overflow-hidden flex items-stretch min-h-[160px] ${!isLocked ? 'hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:hover:border-primary/30 cursor-pointer' : 'opacity-60 grayscale-[0.8] cursor-not-allowed'
                }`}
            >
              {isLocked && (
                <div className="absolute -bottom-6 -right-6 z-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none">
                  <i className="fa-solid fa-lock text-[140px] rotate-12 text-gray-900 dark:text-white"></i>
                </div>
              )}
              <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                <span className="bg-[#cc2b1e] text-[8px] font-black text-white px-1.5 py-0.5 rounded shadow-sm tracking-tighter shadow-red-900/40">PREMIUM</span>
                {isLocked && (
                  <span className="bg-amber-500/90 text-[7px] font-bold text-white px-1.5 py-0.5 rounded shadow-sm uppercase tracking-tighter">
                    {daysRemaining} GÜN SONRA
                  </span>
                )}
              </div>
              <div className="w-1/3 flex items-center justify-center p-2">
                <img src={AlakartImg} alt="Alakart Menü" className="max-h-[110px] w-full object-contain transform group-hover:scale-110 transition-transform duration-300" />
              </div>
              <div className="w-2/3 p-5 flex flex-col justify-center">
                <div className="flex items-center mb-2">
                  <i className="fa-solid fa-wand-magic-sparkles text-lg text-primary mr-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Alakart Menü</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>
                  {isLocked ? `Bu özellik ${daysRemaining} gün sonra sizin ve aileniz için aktif olacak` : 'Modern teknikler ve yaratıcı gurme lezzetler.'}
                </p>
              </div>
            </a>

            {/* 3. Diyet Mutfağı */}
            <a
              href={isLocked ? "#" : "/diet-kitchen"}
              onClick={(e) => {
                if (isLocked) {
                  e.preventDefault();
                  handleLockClick('diet');
                }
              }}
              className={`group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all dark:border-gray-800 dark:bg-[#000] block overflow-hidden flex items-stretch min-h-[160px] ${!isLocked ? 'hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:hover:border-primary/30 cursor-pointer' : 'opacity-60 grayscale-[0.8] cursor-not-allowed'
                }`}
            >
              {isLocked && (
                <div className="absolute -bottom-6 -right-6 z-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none">
                  <i className="fa-solid fa-lock text-[140px] rotate-12 text-gray-900 dark:text-white"></i>
                </div>
              )}
              <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                <span className="bg-[#cc2b1e] text-[8px] font-black text-white px-1.5 py-0.5 rounded shadow-sm tracking-tighter shadow-red-900/40">PREMIUM</span>
                {isLocked && (
                  <span className="bg-amber-500/90 text-[7px] font-bold text-white px-1.5 py-0.5 rounded shadow-sm uppercase tracking-tighter">
                    {daysRemaining} GÜN SONRA
                  </span>
                )}
              </div>
              <div className="w-1/3 flex items-center justify-center p-2">
                <img src={DietImg} alt="Diyet Mutfağı" className="max-h-[110px] w-full object-contain transform group-hover:scale-110 transition-transform duration-300" />
              </div>
              <div className="w-2/3 p-5 flex flex-col justify-center">
                <div className="flex items-center mb-2">
                  <i className="fa-solid fa-apple-whole text-lg text-primary mr-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Diyet Mutfağı</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>
                  {isLocked ? `Bu özellik ${daysRemaining} gün sonra sizin ve aileniz için aktif olacak` : 'Kalori takipli ve sağlıklı yaşam menüleri.'}
                </p>
              </div>
            </a>

            {/* 4. Sporcu Mutfağı */}
            <a
              href={isLocked ? "#" : "/athlete-kitchen"}
              onClick={(e) => {
                if (isLocked) {
                  e.preventDefault();
                  handleLockClick('athlete');
                }
              }}
              className={`group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all dark:border-gray-800 dark:bg-[#000] block overflow-hidden flex items-stretch min-h-[160px] ${!isLocked ? 'hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:hover:border-primary/30 cursor-pointer' : 'opacity-60 grayscale-[0.8] cursor-not-allowed'
                }`}
            >
              {isLocked && (
                <div className="absolute -bottom-6 -right-6 z-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none">
                  <i className="fa-solid fa-lock text-[140px] rotate-12 text-gray-900 dark:text-white"></i>
                </div>
              )}
              <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                <span className="bg-[#cc2b1e] text-[8px] font-black text-white px-1.5 py-0.5 rounded shadow-sm tracking-tighter shadow-red-900/40">PREMIUM</span>
                {isLocked && (
                  <span className="bg-amber-500/90 text-[7px] font-bold text-white px-1.5 py-0.5 rounded shadow-sm uppercase tracking-tighter">
                    {daysRemaining} GÜN SONRA
                  </span>
                )}
              </div>
              <div className="w-1/3 flex items-center justify-center p-2">
                <img src={AthleteImg} alt="Sporcu Mutfağı" className="max-h-[110px] w-full object-contain transform group-hover:scale-110 transition-transform duration-300" />
              </div>
              <div className="w-2/3 p-5 flex flex-col justify-center">
                <div className="flex items-center mb-2">
                  <i className="fa-solid fa-dumbbell text-lg text-primary mr-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Sporcu Mutfağı</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>
                  {isLocked ? `Bu özellik ${daysRemaining} gün sonra sizin ve aileniz için aktif olacak` : 'Performans odaklı, makro dengeli tarifler.'}
                </p>
              </div>
            </a>

            {/* 5. Özel Günler Mutfağı */}
            <a
              href={isLocked ? "#" : "/special-days-kitchen"}
              onClick={(e) => {
                if (isLocked) {
                  e.preventDefault();
                  handleLockClick('special_days');
                }
              }}
              className={`group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all dark:border-gray-800 dark:bg-[#000] block cursor-pointer overflow-hidden flex ${!isLocked ? 'hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:hover:border-primary/30' : 'opacity-60 grayscale-[0.8] cursor-not-allowed'
                }`}
            >
              {isLocked && (
                <div className="absolute -bottom-6 -right-6 z-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none">
                  <i className="fa-solid fa-lock text-[140px] rotate-12 text-gray-900 dark:text-white"></i>
                </div>
              )}
              <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                <span className="bg-[#cc2b1e] text-[8px] font-black text-white px-1.5 py-0.5 rounded shadow-sm tracking-tighter shadow-red-900/40">PREMIUM</span>
                {isLocked && (
                  <span className="bg-amber-500/90 text-[7px] font-bold text-white px-1.5 py-0.5 rounded shadow-sm uppercase tracking-tighter">
                    {daysRemaining} GÜN SONRA
                  </span>
                )}
              </div>
              <div className="w-1/3 flex items-center justify-center p-2">
                <img src={OzelGunlerImg} alt="Özel Günler Mutfağı" className="max-h-[110px] w-full object-contain transform group-hover:scale-110 transition-transform duration-300" />
              </div>
              <div className="w-2/3 p-5 flex flex-col justify-center">
                <div className="flex items-center mb-2">
                  <i className="fa-solid fa-cake-candles text-lg text-primary mr-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Özel Günler Mutfağı</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>
                  {isLocked ? `Bu özellik ${daysRemaining} gün sonra sizin ve aileniz için aktif olacak` : 'Doğum günü ve altın günü için menü, eksik malzeme ve davet akışını birlikte planlayın.'}
                </p>
              </div>
            </a>

            {/* 6. Sağlık+ */}
            <a
              href={isLocked ? "#" : "/health-plus?autostart=1"}
              onClick={(e) => {
                if (isLocked) {
                  e.preventDefault();
                  handleLockClick('health_plus');
                }
              }}
              className={`group relative rounded-2xl border border-[#e6dbdb] bg-background-light transition-all dark:border-gray-800 dark:bg-[#000] block cursor-pointer overflow-hidden flex ${!isLocked ? 'hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:hover:border-primary/30' : 'opacity-60 grayscale-[0.8] cursor-not-allowed'
                }`}
            >
              {isLocked && (
                <div className="absolute -bottom-6 -right-6 z-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none">
                  <i className="fa-solid fa-lock text-[140px] rotate-12 text-gray-900 dark:text-white"></i>
                </div>
              )}
              <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                <span className="bg-[#cc2b1e] text-[8px] font-black text-white px-1.5 py-0.5 rounded shadow-sm tracking-tighter shadow-red-900/40">PREMIUM</span>
                {isLocked && (
                  <span className="bg-amber-500/90 text-[7px] font-bold text-white px-1.5 py-0.5 rounded shadow-sm uppercase tracking-tighter">
                    {daysRemaining} GÜN SONRA
                  </span>
                )}
              </div>
              <div className="w-1/3 flex items-center justify-center p-2">
                <img src={HealthPlusImg} alt="Sağlık+ Mutfağı" className="max-h-[110px] w-full object-contain transform group-hover:scale-110 transition-transform duration-300" />
              </div>
              <div className="w-2/3 p-5 flex flex-col justify-center">
                <div className="flex items-center mb-2">
                  <i className="fa-solid fa-heart-pulse text-lg text-primary mr-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Sağlık+</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: `${0.875 * textScale}rem` }}>
                  {isLocked ? `Bu özellik ${daysRemaining} gün sonra sizin ve aileniz için aktif olacak` : 'Aile ve kişisel sağlık modlarına göre daha dengeli tarifleri öne çıkarır.'}
                </p>
              </div>
            </a>
          </div>
        </section>

        {/* Advertisement Carousel - Area 3 */}
        <section className="w-full">
          <AdCarousel
            areaId={3}
            className="w-full"
            onAdClick={(ad) => console.log('Ad Area 3 clicked:', ad)}
          />
        </section>
      </div >

      {isLunchPreferenceGateActive && (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-[2rem] border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-[#050816]">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200">
              <span className="material-symbols-outlined text-[28px]">lunch_dining</span>
            </div>
            <div className="space-y-2 text-center">
              <p className="text-[11px] font-black uppercase tracking-[0.24em] text-sky-600 dark:text-sky-300">Öğlen Planı</p>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white">Şefim, öğlen yemeğini nasıl planlayayım?</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Dün öğlen hatırlatması istediğin için, öğlenin mutfak planına nasıl dahil olacağını netleştirmem gerekiyor.
              </p>
              {lunchPreferencePrompt?.lunchNotificationTime ? (
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Öğlen hatırlatma saatin: {lunchPreferencePrompt.lunchNotificationTime}
                </p>
              ) : null}
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left dark:border-slate-700 dark:bg-slate-900/60">
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">Öğlen yemeğini kaç kişi için düzenleyeyim?</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                {LUNCH_SERVING_OPTIONS.map((option) => {
                  const selected = lunchServingSelection === option.value;
                  return (
                    <button
                      key={String(option.value)}
                      type="button"
                      onClick={() => setLunchServingSelection(option.value)}
                      className={`rounded-2xl border px-3 py-3 text-left text-sm font-bold transition ${
                        selected
                          ? 'border-sky-500 bg-sky-600 text-white shadow-lg shadow-sky-500/20'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-sky-300 dark:border-slate-700 dark:bg-[#0d1326] dark:text-slate-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-xs font-medium text-slate-500 dark:text-slate-400">{lunchServingMeaningText}</p>
            </div>

            <div className="mt-5 grid gap-3">
              {LUNCH_MODE_OPTIONS.map((option) => {
                const selected = lunchPreferenceMode === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setLunchPreferenceMode(option.value)}
                    className={`rounded-[1.6rem] border px-4 py-4 text-left transition ${
                      selected
                        ? 'border-sky-500 bg-sky-50 shadow-lg shadow-sky-500/10 dark:border-sky-400 dark:bg-sky-900/20'
                        : 'border-slate-200 bg-white hover:border-sky-200 dark:border-slate-700 dark:bg-[#0b0f1d]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-black text-slate-900 dark:text-white">{option.label}</p>
                        <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">{option.description}</p>
                      </div>
                      {option.badge ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-red-600 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                          <span className="material-symbols-outlined text-[14px]">warning</span>
                          {option.badge}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>

            {lunchPreferenceMode === 'leftover_only' && (
              <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
                Öğlen için ayrı menü açmayacağım. Saat geldiğinde yalnızca yumuşak bir hatırlatma göndereceğim.
              </p>
            )}

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleSaveLunchPreference}
                disabled={savingLunchPreference}
                className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-sky-600/20 transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingLunchPreference ? 'Kaydediliyor...' : 'Tercihimi Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modern Lockdown Info Modal */}
      {
        modalContent && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity" onClick={() => setModalContent(null)}></div>
            <div className="relative w-full max-w-lg bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl overflow-hidden border border-[#e6dbdb] dark:border-gray-800 animate-in zoom-in-95 duration-200">
              <div className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <i className={`fa-solid ${modalContent.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 dark:text-white">{modalContent.title}</h3>
                </div>

                <div className="space-y-4">
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg italic bg-gray-50 dark:bg-gray-800/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-700">
                    {modalContent.message}
                  </p>

                  <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-100 dark:border-amber-900/30 text-amber-700 dark:text-amber-400">
                    <i className="fa-solid fa-lock text-xl"></i>
                    <span className="text-sm font-bold uppercase tracking-wider">BU ÖZELLİK {daysRemaining} GÜN SONRA ŞEFLİĞİNİZE AÇILACAK</span>
                  </div>
                </div>

                <button
                  onClick={() => setModalContent(null)}
                  className="mt-8 w-full py-4 bg-gray-900 dark:bg-white text-white dark:text-black font-black rounded-2xl hover:scale-[1.02] active:scale-95 transition-all shadow-xl"
                >
                  TAMAM
                </button>
              </div>
            </div>
          </div>
        )
      }
      {showBabyDetailModal && (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-[#e6dbdb] bg-white p-5 shadow-2xl dark:border-gray-700 dark:bg-[#111]">
            <h3 className="text-lg font-extrabold text-gray-900 dark:text-white">Bebeğini daha iyi tanıyalım</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Tarifleri daha doğru kişiselleştirmek için isim ve doğum tarihini paylaşabilirsin.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-bold text-gray-700 dark:text-gray-300">Bebek adı</label>
                <input
                  type="text"
                  value={babyDetailName}
                  onChange={(e) => setBabyDetailName(e.target.value)}
                  maxLength={40}
                  placeholder="Örn: Defne"
                  className="w-full rounded-xl border border-[#e6dbdb] bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-primary dark:border-gray-700 dark:bg-[#0b0b0b] dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-gray-700 dark:text-gray-300">Doğum tarihi</label>
                <input
                  type="date"
                  max={new Date().toISOString().split('T')[0]}
                  value={babyDetailBirthDate}
                  onChange={(e) => setBabyDetailBirthDate(e.target.value)}
                  className="w-full rounded-xl border border-[#e6dbdb] bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-primary dark:border-gray-700 dark:bg-[#0b0b0b] dark:text-white"
                />
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={handleSaveBabyDetail}
                disabled={savingBabyDetail || !String(babyDetailName || '').trim() || !String(babyDetailBirthDate || '').trim()}
                className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingBabyDetail ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button
                type="button"
                onClick={() => incrementBabyDetailDeferral('later')}
                className="rounded-xl border border-[#e6dbdb] bg-white px-4 py-2 text-sm font-bold text-slate-700 dark:border-gray-700 dark:bg-[#0b0b0b] dark:text-slate-200"
              >
                Daha Sonra
              </button>
              <button
                type="button"
                onClick={() => incrementBabyDetailDeferral('decline')}
                className="rounded-xl border border-[#e6dbdb] bg-white px-4 py-2 text-sm font-bold text-slate-700 dark:border-gray-700 dark:bg-[#0b0b0b] dark:text-slate-200"
              >
                Bu Bilgiyi Girmek İstemiyorum
              </button>
            </div>

            {babyDetailDeferralCount === 1 && (
              <p className="mt-3 text-xs font-semibold text-amber-700 dark:text-amber-300">
                Sorun değil. Bir kez daha hatırlatacağız, sonra bugünlük tekrar göstermeyeceğiz.
              </p>
            )}
          </div>
        </div>
      )}
      {breakfastModalOpen && (
        <BreakfastRecommendationModal
          isOpen={breakfastModalOpen}
          onClose={() => setBreakfastModalOpen(false)}
          onComplete={(data) => {
            setBreakfastCompletedDayKey(currentDayKey);
            setBreakfastModalOpen(false);
            setBreakfastActionPayload((prev) => ({
              ...(prev || {}),
              ...(data || {}),
              status: 'completed',
              suggestions: [],
              dashboardTriggerState: {
                ...(prev?.dashboardTriggerState || {}),
                ...(data?.dashboardTriggerState || {})
              }
            }));
            refreshMenuCompletionState?.();
            fetchDashboardTodayViews();
          }}
        />
      )}
      {selectedBreakfastRecipe && (
        <RecipeModal
          isOpen={breakfastRecipeModalOpen}
          onClose={() => setBreakfastRecipeModalOpen(false)}
          recipe={selectedBreakfastRecipe}
          onPairingClick={handleBreakfastRecipeOpen}
          onMarkAsUsed={handleBreakfastRecipeMarkAsUsed}
          userProfile={userProfileData}
          userPortion={Number(breakfastActionPayload?.requestedServingSize || userProfileData?.planning_breakfast_servings || userProfileData?.servingSize || 1) || 1}
          skillLevel={dashboardSkillLevel}
          isDarkMode={isDarkMode}
        />
      )}
    </DashboardLayout >
  );
};

export default OnboardingLanding;
